/**
 * The wide bank.
 *
 * Generated by `npm run content:import` from a plain-text question list.
 * Do not hand-edit: corrections belong in scripts/import-questions.ts, which
 * is what a re-run reads. Every entry here has passed `npm run content:check`.
 */

import type { Question } from '@trivia/shared';

export const wideQuestions: Question[] = [
  {
    id: 'wide.002-how-many-earths-could-fit',
    prompt: 'Approximately how many Earths could fit inside the Sun by volume?',
    category: 'Space',
    difficulty: 3,
    reveal: {
      explanation: 'The Sun is only about 109 Earths across, but volume grows in three dimensions — so roughly 1.3 million Earths could fit inside it.',
      headline: 'About 1.3 million Earths',
    },
    source: {
      citation: 'NASA',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 1300000,
    displayAnswer: 'About 1.3 million Earths',
    unit: 'Earths',
    magnitude: 'millions',
  },
  {
    id: 'wide.003-what-average-depth-world-oceans',
    prompt: 'What is the average depth of the world\'s oceans?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'The average ocean depth is about 3,682 metres. The deepest point is almost three times deeper than that.',
      headline: 'About 3.7 km',
    },
    source: {
      citation: 'NOAA',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 3.7,
    displayAnswer: 'About 3.7 km',
    unit: 'km',
    magnitude: 'small',
  },
  {
    id: 'wide.004-how-many-potentially-active-volcanoes',
    prompt: 'Approximately how many potentially active volcanoes are there on Earth, excluding the continuous volcanic belts on the ocean floor?',
    category: 'Geography',
    difficulty: 4,
    reveal: {
      explanation: 'Around 1,350 volcanoes are considered potentially active, and roughly 500 have erupted during recorded history.',
      headline: 'About 1,350',
    },
    source: {
      citation: 'U.S. Geological Survey',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 1350,
    displayAnswer: 'About 1,350',
    magnitude: 'thousands',
  },
  {
    id: 'wide.005-what-percentage-earth-freshwater-locked',
    prompt: 'What percentage of Earth\'s freshwater is locked in glaciers, ice caps, and permanent snow?',
    category: 'Earth',
    difficulty: 4,
    reveal: {
      explanation: 'Most freshwater isn\'t in lakes or rivers. Roughly 68.7% is frozen in glaciers and ice caps.',
      headline: 'About 69%',
    },
    source: {
      citation: 'U.S. Geological Survey',
      kind: 'definitional',
    },
    type: 'percentage',
    answer: 69,
    displayAnswer: 'About 69%',
  },
  {
    id: 'wide.006-seasonal-peak-how-many-worker',
    prompt: 'At its seasonal peak, roughly how many worker bees can live in a strong honey-bee colony?',
    category: 'Animals',
    difficulty: 3,
    reveal: {
      explanation: 'A strong colony can reach roughly 60,000 workers, plus a queen and hundreds or thousands of drones depending on the season.',
      headline: 'About 50,000–60,000 workers',
    },
    source: {
      citation: 'University of Delaware / Oregon State Extension',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 54800,
    displayAnswer: 'About 50,000–60,000 workers',
    unit: 'workers',
    magnitude: 'thousands',
  },
  {
    id: 'wide.007-which-contains-water-earth-atmosphere',
    prompt: 'Which contains more water: Earth\'s atmosphere or all of Earth\'s rivers?',
    category: 'Earth',
    difficulty: 4,
    reveal: {
      explanation: 'The atmosphere contains roughly 12,900 km³ of water, while all rivers contain only about 2,120 km³ at a given time.',
      headline: 'The atmosphere, by about 6×',
    },
    source: {
      citation: 'U.S. Geological Survey',
      kind: 'definitional',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Earth\'s atmosphere',
      },
      {
        label: 'All of Earth\'s rivers',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.008-during-feeding-season-how-much',
    prompt: 'During its feeding season, roughly how much krill can an adult blue whale eat in one day?',
    category: 'Animals',
    difficulty: 4,
    reveal: {
      explanation: 'Research on eastern North Pacific blue whales estimated consumption of about 16 tonnes of krill per feeding day.',
      headline: 'About 16 metric tonnes',
    },
    source: {
      citation: 'Smithsonian Institution / Nature research',
      kind: 'estimated',
    },
    type: 'numeric',
    answer: 16,
    displayAnswer: 'About 16 metric tonnes',
    unit: 'metric tonnes',
    magnitude: 'small',
  },
  {
    id: 'wide.010-how-many-neurons-human-brain',
    prompt: 'Approximately how many neurons are in the human brain?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'The commonly used estimate is about 86 billion neurons. Surprisingly, most of them are in the cerebellum rather than the cerebral cortex.',
      headline: 'About 86 billion',
    },
    source: {
      citation: 'Neuroscience literature / NIH-supported research',
      kind: 'estimated',
    },
    type: 'numeric',
    answer: 86000000000,
    displayAnswer: 'About 86 billion',
    magnitude: 'billions',
  },
  {
    id: 'wide.011-how-many-countries-world-completely',
    prompt: 'Approximately how many countries in the world are completely landlocked?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Forty-four sovereign states have no direct access to an open sea or ocean. Only 32 of those are classified by the UN as landlocked developing countries.',
      headline: '44 countries',
    },
    source: {
      citation: 'United Nations',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 44,
    displayAnswer: '44 countries',
    unit: 'countries',
    magnitude: 'small',
  },
  {
    id: 'wide.012-which-wider-australia-from-east',
    prompt: 'Which is wider: Australia from east to west, or the diameter of the Moon?',
    category: 'Space',
    difficulty: 3,
    reveal: {
      explanation: 'Australia is almost 4,000 km wide east-to-west. The Moon is only about 3,476 km across. Australia is therefore roughly 500 km wider.',
      headline: 'Australia',
    },
    source: {
      citation: 'Geoscience Australia / NASA',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Australia from east to west',
      },
      {
        label: 'The diameter of the Moon',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.013-which-has-larger-surface-area',
    prompt: 'Which has the larger surface area: Russia or Pluto?',
    category: 'Space',
    difficulty: 4,
    reveal: {
      explanation: 'Pluto\'s surface area is about 17.65 million km². Russia covers about 17.10 million km². Pluto wins by only around 3%.',
      headline: 'Pluto — just barely',
    },
    source: {
      citation: 'NASA / CIA World Factbook',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Russia',
      },
      {
        label: 'Pluto',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.014-which-mountain-summit-farther-from',
    prompt: 'Which mountain summit is farther from the center of Earth: Mount Everest or Mount Chimborazo?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Everest is higher above sea level, but Earth bulges around the equator. Chimborazo sits near that bulge, putting its summit about 2.1 km farther from Earth\'s center than Everest\'s.',
      headline: 'Mount Chimborazo',
    },
    source: {
      citation: 'NOAA',
      kind: 'measured',
    },
    type: 'which-is-closer',
    options: [
      {
        label: 'Mount Everest',
      },
      {
        label: 'Mount Chimborazo',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.017-woolly-mammoths-still-alive-after',
    prompt: 'Were woolly mammoths still alive after the Great Pyramid of Giza had already been built?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'The Great Pyramid was completed around the mid-26th century BCE. Isolated woolly mammoths survived on Wrangel Island until roughly 4,000 years ago — centuries after the pyramid was standing.',
      headline: 'Yes',
    },
    source: {
      citation: 'Smithsonian / Natural History Museum',
      kind: 'measured',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 0,
  },
  {
    id: 'wide.018-you-re-game-show-with',
    prompt: 'You\'re on a game show with three doors. One hides a prize. You choose a door. The host, who knows where the prize is, deliberately opens one losing door and offers you the chance to switch. What is your chance of winning if you switch?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'Your original choice had only a 1/3 chance. The other two doors collectively had 2/3; once the host deliberately removes a losing one, that 2/3 probability effectively concentrates on the remaining door.',
      headline: '2/3, or about 66.7%',
    },
    source: {
      citation: 'Mathematical result — Monty Hall problem',
      kind: 'definitional',
    },
    type: 'probability',
    answer: 66.7,
    displayAnswer: '2/3, or about 66.7%',
  },
  {
    id: 'wide.019-if-you-roll-fair-six',
    prompt: 'If you roll a fair six-sided die four times, what is the probability of rolling at least one six?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'It\'s slightly more likely than not. The easiest route is to calculate the opposite: the chance of avoiding a six four times is (5/6)^4 ≈ 48.2%, leaving about 51.8%.',
      headline: 'About 51.8%',
    },
    source: {
      citation: 'Calculated probability',
      kind: 'definitional',
    },
    type: 'probability',
    answer: 51.8,
    displayAnswer: 'About 51.8%',
  },
  {
    id: 'wide.020-you-have-8-identical-looking',
    prompt: 'You have 8 identical-looking balls. One is heavier than the other seven. Using only a balance scale, what is the minimum number of weighings needed to guarantee finding the heavy ball?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'Weigh 3 against 3. If one side is heavier, the odd ball is among those three; otherwise it\'s among the two left out. One more weighing resolves either case.',
      headline: '2 weighings',
    },
    source: {
      citation: 'Logical deduction',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 2,
    displayAnswer: '2 weighings',
    unit: 'weighings',
    magnitude: 'small',
  },
  {
    id: 'wide.021-three-switches-outside-closed-room',
    prompt: 'Three switches are outside a closed room. Exactly one controls an incandescent bulb inside. You may manipulate the switches however you like, but you may enter the room only once. Can you determine which switch controls the bulb?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'Turn switch A on for several minutes, then turn it off. Turn B on and enter. If the bulb is lit, B controls it. If it\'s off but warm, A controls it. If it\'s off and cold, C controls it.',
      headline: 'Yes',
    },
    source: {
      citation: 'Logical puzzle',
      kind: 'definitional',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 0,
  },
  {
    id: 'wide.023-how-many-times-larger-africa',
    prompt: 'Approximately how many times larger is Africa than Greenland by area?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Africa is roughly 30.1 million km², while Greenland is about 2.17 million km². Mercator-style maps make Greenland look dramatically larger than it really is.',
      headline: 'About 14× larger',
    },
    source: {
      citation: 'CIA World Factbook',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 14,
    displayAnswer: 'About 14× larger',
    unit: '×',
    magnitude: 'small',
  },
  {
    id: 'wide.024-what-percentage-world-fresh-surface',
    prompt: 'Roughly what percentage of the world\'s fresh surface water is contained in Lake Baikal?',
    category: 'Geography',
    difficulty: 4,
    reveal: {
      explanation: 'A single Siberian lake contains roughly one-fifth of Earth\'s fresh surface water by volume. Lake Baikal is also the world\'s deepest freshwater lake.',
      headline: 'About 20%',
    },
    source: {
      citation: 'U.S. Geological Survey',
      kind: 'definitional',
    },
    type: 'percentage',
    answer: 20,
    displayAnswer: 'About 20%',
  },
  {
    id: 'wide.026-which-longer-great-wall-china',
    prompt: 'Which is longer: the Great Wall of China or Earth\'s diameter?',
    category: 'Earth',
    difficulty: 3,
    reveal: {
      explanation: 'The Great Wall system stretches for more than 20,000 km in total. Earth is only about 12,756 km across at the equator.',
      headline: 'The Great Wall, by roughly 1.6×',
    },
    source: {
      citation: 'UNESCO / NASA',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The Great Wall of China',
      },
      {
        label: 'Earth\'s diameter',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.027-how-much-blood-does-human',
    prompt: 'Roughly how much blood does the human heart pump in one day?',
    category: 'People',
    difficulty: 4,
    reveal: {
      explanation: 'The American Heart Association gives a figure of almost 2,000 gallons per day — around 7,500 litres. The heart repeatedly circulates the same relatively small blood supply.',
      headline: 'About 7,500 litres',
    },
    source: {
      citation: 'American Heart Association',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 7500,
    displayAnswer: 'About 7,500 litres',
    unit: 'litres',
    magnitude: 'thousands',
  },
  {
    id: 'wide.028-if-you-could-peel-off',
    prompt: 'If you could peel off and lay out an adult person\'s skin flat, roughly how much area would it cover?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'About the area of a fairly large tabletop or small bedsheet. Skin is also the body\'s largest organ.',
      headline: 'Around 1.5–2 m²',
    },
    source: {
      citation: 'NCBI / InformedHealth',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 1.73,
    displayAnswer: 'Around 1.5–2 m²',
    unit: 'm²',
    magnitude: 'small',
  },
  {
    id: 'wide.031-which-has-surface-area-pacific',
    prompt: 'Which has more surface area: the Pacific Ocean or all of Earth\'s continents combined?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'The Pacific covers more than 155 million km² and is actually larger than the total land area of all Earth\'s continents combined.',
      headline: 'The Pacific Ocean',
    },
    source: {
      citation: 'NOAA',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The Pacific Ocean',
      },
      {
        label: 'All of Earth\'s continents combined',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.032-which-longer-venus-one-full',
    prompt: 'Which is longer on Venus: one full rotation on its axis or one trip around the Sun?',
    category: 'Space',
    difficulty: 3,
    reveal: {
      explanation: 'Venus takes about 243 Earth days to rotate once, but only about 225 Earth days to orbit the Sun.',
      headline: 'One rotation — a Venus day is longer than a Venus year.',
    },
    source: {
      citation: 'NASA',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'One full rotation on its axis',
      },
      {
        label: 'One trip around the Sun',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.034-how-long-after-invention-tin',
    prompt: 'Roughly how long after the invention of the tin food can was the first purpose-built can opener patented?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Tin food cans appeared around 1810, but Ezra Warner\'s dedicated can opener wasn\'t patented until 1858. Before then, people commonly attacked cans with knives and other tools.',
      headline: 'About 48 years later',
    },
    source: {
      citation: 'U.S. National Archives / Smithsonian',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 48,
    displayAnswer: 'About 48 years later',
    unit: 'years later',
    magnitude: 'small',
  },
  {
    id: 'wide.035-which-came-first-traditional-first',
    prompt: 'Which came first: the traditional first ancient Olympic Games or the traditional founding of Rome?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'The traditional date of the first Olympic Games is 776 BCE. Rome\'s traditional founding date is 753 BCE — only about 23 years later.',
      headline: 'The Olympic Games',
    },
    source: {
      citation: 'Historical chronology',
      kind: 'definitional',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The traditional first ancient Olympic Games',
      },
      {
        label: 'The traditional founding of Rome',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.036-genghis-khan-lived-closer-time',
    prompt: 'Genghis Khan lived closer in time to which event: the Great Pyramid of Giza being built, or the Moon landing?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Genghis Khan was born around 1162 CE. Apollo 11 landed in 1969, about 800 years later; the Great Pyramid was already roughly 3,700 years old when he was born.',
      headline: 'The Moon landing',
    },
    source: {
      citation: 'Historical chronology',
      kind: 'definitional',
    },
    type: 'which-is-closer',
    options: [
      {
        label: 'The Great Pyramid of Giza being built',
      },
      {
        label: 'The Moon landing',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.037-how-much-blood-inside-average',
    prompt: 'Roughly how much blood is inside an average adult human body at one time?',
    category: 'People',
    difficulty: 2,
    reveal: {
      explanation: 'Despite the heart pumping thousands of litres per day, the body only contains roughly five litres at once — it simply keeps circulating it.',
      headline: 'About 5 litres',
    },
    source: {
      citation: 'NCBI',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 5,
    displayAnswer: 'About 5 litres',
    unit: 'litres',
    magnitude: 'small',
  },
  {
    id: 'wide.038-what-percentage-bones-adult-human',
    prompt: 'Roughly what percentage of all the bones in an adult human body are located in the hands and feet?',
    category: 'People',
    difficulty: 4,
    reveal: {
      explanation: 'The two hands contain 54 bones and the two feet contain 52, for 106 out of about 206 bones in the adult skeleton. More than half your bones are in your hands and feet.',
      headline: 'About 51%',
    },
    source: {
      citation: 'NCBI / anatomical reference',
      kind: 'measured',
    },
    type: 'percentage',
    answer: 51,
    displayAnswer: 'About 51%',
  },
  {
    id: 'wide.039-during-breeding-how-long-can',
    prompt: 'During breeding, how long can a male emperor penguin go without eating?',
    category: 'Animals',
    difficulty: 4,
    reveal: {
      explanation: 'Male emperor penguins can fast for roughly 120 days while courting and incubating their egg through the Antarctic winter.',
      headline: 'Up to about 4 months',
    },
    source: {
      citation: 'Nature / physiological research',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 4,
    displayAnswer: 'Up to about 4 months',
    unit: 'months',
    magnitude: 'small',
  },
  {
    id: 'wide.040-during-flight-how-fast-can',
    prompt: 'During flight, roughly how fast can a hummingbird\'s heart beat?',
    category: 'Animals',
    difficulty: 4,
    reveal: {
      explanation: 'A ruby-throated hummingbird may go from around 225 beats per minute at rest to more than 1,200 bpm in flight.',
      headline: 'More than 1,200 beats per minute',
    },
    source: {
      citation: 'Smithsonian National Zoo',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 1200,
    displayAnswer: 'More than 1,200 beats per minute',
    unit: 'beats per minute',
    magnitude: 'thousands',
  },
  {
    id: 'wide.041-how-far-can-arctic-tern',
    prompt: 'Approximately how far can an Arctic tern travel during one annual migration cycle?',
    category: 'Animals',
    difficulty: 4,
    reveal: {
      explanation: 'That\'s roughly 1.75 trips around Earth\'s circumference every year for a bird weighing only around 100 grams.',
      headline: 'Around 70,000 km, with some tracked birds exceeding 80,000 km',
    },
    source: {
      citation: 'British Antarctic Survey / PNAS',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 70000,
    displayAnswer: 'Around 70,000 km, with some tracked birds exceeding 80,000 km',
    unit: 'km',
    magnitude: 'thousands',
  },
  {
    id: 'wide.042-deepest-part-mariana-trench-how',
    prompt: 'At the deepest part of the Mariana Trench, roughly how many times greater is the pressure than at sea level?',
    category: 'Geography',
    difficulty: 4,
    reveal: {
      explanation: 'Near Challenger Deep, pressure reaches roughly 1,080 atmospheres. Every small patch of surface is being crushed by kilometres of water above it.',
      headline: 'About 1,000×',
    },
    source: {
      citation: 'NOAA',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 1000,
    displayAnswer: 'About 1,000×',
    unit: '×',
    magnitude: 'thousands',
  },
  {
    id: 'wide.043-imagine-ordinary-empty-bedroom-measuring',
    prompt: 'Imagine an ordinary empty bedroom measuring 4 m × 4 m × 2.5 m. Roughly how much does all the air inside it weigh?',
    category: 'Science',
    difficulty: 4,
    reveal: {
      explanation: 'The room contains about 40 m³ of air. Near sea level, air has a density of roughly 1.2 kg/m³, putting the total mass around 50 kg. Air feels weightless because we\'re immersed in it, but it absolutely has mass.',
      headline: 'About 50 kg',
    },
    source: {
      citation: 'Calculated estimate from standard air density',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 50,
    displayAnswer: 'About 50 kg',
    unit: 'kg',
    magnitude: 'small',
  },
  {
    id: 'wide.044-how-many-golf-balls-could',
    prompt: 'Roughly how many golf balls could fit inside the passenger compartment of an empty full-size school bus?',
    category: 'Everyday',
    difficulty: 4,
    reveal: {
      explanation: 'A golf ball is only about 4.3 cm across. With roughly 30 m³ of usable interior volume and realistic random sphere packing, the answer lands near half a million.',
      headline: 'About 500,000 golf balls',
    },
    source: {
      citation: 'Calculated Fermi estimate',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 500000,
    displayAnswer: 'About 500,000 golf balls',
    unit: 'golf balls',
    magnitude: 'thousands',
  },
  {
    id: 'wide.045-if-people-stood-tightly-shoulder',
    prompt: 'If people stood tightly shoulder-to-shoulder on a regulation soccer pitch, roughly how many people could fit?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'A standard 105 × 68 m pitch is about 7,140 m². At roughly 4 people per square metre, you get close to 29,000 people.',
      headline: 'Around 25,000–30,000 people',
    },
    source: {
      citation: 'Calculated estimate',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 27400,
    displayAnswer: 'Around 25,000–30,000 people',
    unit: 'people',
    magnitude: 'thousands',
  },
  {
    id: 'wide.046-three-boxes-labeled-apples-oranges',
    prompt: 'Three boxes are labeled APPLES, ORANGES, and MIXED. Every label is wrong. You may take one piece of fruit from only one box. What is the minimum number of fruits you need to draw to correctly relabel all three boxes?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'Draw from the box labeled MIXED. Because every label is wrong, that box cannot actually be mixed. One fruit tells you whether it contains only apples or only oranges, and the other two labels can then be deduced.',
      headline: '1 fruit',
    },
    source: {
      citation: 'Logical deduction',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 1,
    displayAnswer: '1 fruit',
    unit: 'fruit',
    magnitude: 'small',
  },
  {
    id: 'wide.047-there-100-closed-doors-person',
    prompt: 'There are 100 closed doors. Person 1 toggles every door, person 2 toggles every second door, person 3 every third door, and so on through person 100. How many doors are open at the end?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'A door is toggled once for every divisor of its number. Most numbers have divisors in pairs, so they\'re toggled an even number of times. Only perfect squares have an odd number of divisors. Between 1 and 100 there are 10 perfect squares.',
      headline: '10 doors',
    },
    source: {
      citation: 'Mathematical deduction',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 10,
    displayAnswer: '10 doors',
    unit: 'doors',
    magnitude: 'small',
  },
  {
    id: 'wide.048-you-have-25-horses-track',
    prompt: 'You have 25 horses, a track that can race only 5 horses at once, and no stopwatch. You need to identify the 3 fastest horses. What is the minimum number of races required?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'Race five groups first: 5 races. Race the five winners: race 6. That eliminates most horses from contention. One final race among the only remaining possible second- and third-place horses determines the top three.',
      headline: '7 races',
    },
    source: {
      citation: 'Logical optimization puzzle',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 7,
    displayAnswer: '7 races',
    unit: 'races',
    magnitude: 'small',
  },
  {
    id: 'wide.049-with-two-ordinary-six-sided',
    prompt: 'With two ordinary six-sided dice, which total is more likely: 7 or 8?',
    category: 'Logic',
    difficulty: 2,
    reveal: {
      explanation: 'There are 6 combinations that make 7, but only 5 combinations that make 8. Our brains often treat nearby totals as equally likely even though dice sums aren\'t distributed evenly.',
      headline: '7',
    },
    source: {
      citation: 'Calculated probability',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 7,
    displayAnswer: '7',
    magnitude: 'small',
  },
  {
    id: 'wide.050-you-repeatedly-flip-fair-coin',
    prompt: 'You repeatedly flip a fair coin until you get two heads in a row. On average, how many flips will it take?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'It feels like it should be around four, because HH has a 1-in-4 chance across any two flips. But failed attempts overlap in awkward ways — especially when a tail resets your progress. The true expected waiting time is 6 flips.',
      headline: '6 flips',
    },
    source: {
      citation: 'Calculated probability / expected-value result',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 6,
    displayAnswer: '6 flips',
    unit: 'flips',
    magnitude: 'small',
  },
  {
    id: 'wide.051-how-much-does-earth-atmosphere',
    prompt: 'Approximately how much does Earth\'s entire atmosphere weigh?',
    category: 'Earth',
    difficulty: 4,
    reveal: {
      explanation: 'Air feels almost weightless locally, but the entire atmosphere has a mass of roughly 5.15 × 10¹⁸ kg.',
      headline: 'About 5.15 quintillion kg',
    },
    source: {
      citation: 'Journal of Geophysical Research / NCAR',
      kind: 'estimated',
    },
    type: 'numeric',
    answer: 5150000000000000000,
    displayAnswer: 'About 5.15 quintillion kg',
    unit: 'kg',
    magnitude: 'astronomical',
  },
  {
    id: 'wide.052-how-much-material-from-space',
    prompt: 'Roughly how much material from space reaches Earth every day as cosmic dust and tiny meteoric particles?',
    category: 'Space',
    difficulty: 4,
    reveal: {
      explanation: 'Most incoming extraterrestrial material isn\'t a dramatic meteorite — it\'s microscopic dust. NASA-supported estimates converge around 25 tonnes reaching Earth each day.',
      headline: 'About 25 metric tonnes per day',
    },
    source: {
      citation: 'NASA',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 25,
    displayAnswer: 'About 25 metric tonnes per day',
    magnitude: 'small',
  },
  {
    id: 'wide.054-if-someone-moon-flashed-extremely',
    prompt: 'If someone on the Moon flashed an extremely powerful laser toward Earth, roughly how long would the light take to reach us?',
    category: 'Space',
    difficulty: 3,
    reveal: {
      explanation: 'The Moon averages about 384,400 km away, but light crosses that enormous-looking gap in only about 1.3 seconds.',
      headline: 'About 1.3 seconds',
    },
    source: {
      citation: 'NASA',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 1.3,
    displayAnswer: 'About 1.3 seconds',
    unit: 'seconds',
    magnitude: 'small',
  },
  {
    id: 'wide.056-how-many-earths-could-you',
    prompt: 'Roughly how many Earths could you line up side-by-side across Jupiter\'s diameter?',
    category: 'Space',
    difficulty: 3,
    reveal: {
      explanation: 'Jupiter is about 11 times wider than Earth. But because volume grows much faster than diameter, more than 1,300 Earths could fit inside it by volume.',
      headline: 'About 11 Earths',
    },
    source: {
      citation: 'NASA',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 11,
    displayAnswer: 'About 11 Earths',
    unit: 'Earths',
    magnitude: 'small',
  },
  {
    id: 'wide.057-how-long-elephant-pregnant',
    prompt: 'Roughly how long is an elephant pregnant?',
    category: 'Animals',
    difficulty: 3,
    reveal: {
      explanation: 'Nearly two years. Elephants have the longest gestation period of any living mammal.',
      headline: 'About 22 months',
    },
    source: {
      citation: 'San Diego Zoo Wildlife Alliance',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 22,
    displayAnswer: 'About 22 months',
    unit: 'months',
    magnitude: 'small',
  },
  {
    id: 'wide.058-how-old-can-greenland-shark',
    prompt: 'Roughly how old can a Greenland shark get?',
    category: 'Animals',
    difficulty: 4,
    reveal: {
      explanation: 'Greenland sharks are the longest-lived known vertebrates. One major age estimate put a large individual around 392 years old, although with substantial uncertainty.',
      headline: 'Around 400 years',
    },
    source: {
      citation: 'Smithsonian / scientific research',
      kind: 'estimated',
    },
    type: 'numeric',
    answer: 400,
    displayAnswer: 'Around 400 years',
    unit: 'years',
    magnitude: 'small',
  },
  {
    id: 'wide.059-how-much-poop-can-adult',
    prompt: 'Roughly how much poop can an adult elephant produce in one day?',
    category: 'Animals',
    difficulty: 4,
    reveal: {
      explanation: 'An elephant can deposit around its own human-sized person\'s weight in poop every day.',
      headline: 'Around 136 kg',
    },
    source: {
      citation: 'San Diego Zoo Wildlife Alliance',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 136,
    displayAnswer: 'Around 136 kg',
    unit: 'kg',
    magnitude: 'small',
  },
  {
    id: 'wide.060-during-hunting-dive-how-fast',
    prompt: 'During a hunting dive, roughly how fast can a peregrine falcon travel?',
    category: 'Animals',
    difficulty: 4,
    reveal: {
      explanation: 'Peregrines are generally considered the fastest animals on Earth during their diving attack. Measurements have reached roughly 238 mph (383 km/h).',
      headline: 'Around 380 km/h',
    },
    source: {
      citation: 'Cornell Lab of Ornithology',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 380,
    displayAnswer: 'Around 380 km/h',
    unit: 'km/h',
    magnitude: 'small',
  },
  {
    id: 'wide.061-through-actual-land-territory-how',
    prompt: 'Through the actual land territory of how many countries does Earth\'s Equator pass?',
    category: 'Geography',
    difficulty: 4,
    reveal: {
      explanation: 'The Equator intersects 11 countries on land. If territorial waters of Kiribati and the Maldives are counted too, the commonly quoted total becomes 13.',
      headline: '11 countries',
    },
    source: {
      citation: 'Geographic reference data',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 11,
    displayAnswer: '11 countries',
    unit: 'countries',
    magnitude: 'small',
  },
  {
    id: 'wide.062-when-colosseum-opened-rome-around',
    prompt: 'When the Colosseum opened in Rome around 80 CE, was the Great Pyramid of Giza already older than the Colosseum is today?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'The Great Pyramid was already roughly 2,600 years old when the Colosseum opened. The Colosseum today is only about 1,950 years old. Ancient Egypt was ancient even to the Romans.',
      headline: 'Yes',
    },
    source: {
      citation: 'Oxford Classical Dictionary / standard Egyptian chronology',
      kind: 'definitional',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 0,
  },
  {
    id: 'wide.063-from-augustus-becoming-rome-first',
    prompt: 'From Augustus becoming Rome\'s first emperor in 27 BCE to the conventional fall of the Western Roman Empire in 476 CE, roughly how long did the Roman Empire in the West last?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Roughly five centuries separate Augustus from the conventional fall of the Western Empire. The Eastern Roman Empire continued much longer afterward.',
      headline: 'About 500 years',
    },
    source: {
      citation: 'National Geographic historical chronology',
      kind: 'definitional',
    },
    type: 'numeric',
    answer: 500,
    displayAnswer: 'About 500 years',
    unit: 'years',
    magnitude: 'small',
  },
  {
    id: 'wide.064-four-people-must-cross-bridge',
    prompt: 'Four people must cross a bridge at night. Their crossing times are 1, 2, 7, and 10 minutes. Only two can cross at once, they need one flashlight, and a pair moves at the slower person\'s speed. What is the fastest possible time for everyone to cross?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'The trick is making the two slowest cross together: 1 & 2 cross = 2 1 returns = 1 7 & 10 cross = 10 2 returns = 2 1 & 2 cross = 2',
      headline: '17 minutes',
    },
    type: 'numeric',
    answer: 17,
    displayAnswer: '17 minutes',
    unit: 'minutes',
    magnitude: 'small',
  },
  {
    id: 'wide.065-there-100-passengers-boarding-plane',
    prompt: 'There are 100 passengers boarding a plane with assigned seats. Passenger 1 ignores their assignment and chooses a random seat. Every later passenger takes their own seat if available; otherwise they choose a random remaining seat. What is the probability passenger 100 gets their assigned seat?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'Eventually the random chain must hit either passenger 1\'s seat or passenger 100\'s seat. Those two outcomes are symmetric, giving the final passenger a 1-in-2 chance.',
      headline: '50%',
    },
    type: 'probability',
    answer: 50,
    displayAnswer: '50%',
  },
  {
    id: 'wide.066-you-have-unmarked-3-litre',
    prompt: 'You have an unmarked 3-litre jug and an unmarked 5-litre jug, plus unlimited water. Can you measure exactly 4 litres?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'Fill the 5 L jug and pour into the 3 L jug, leaving 2 L. Empty the 3 L jug and transfer those 2 L into it. Refill the 5 L jug, then pour only enough into the 3 L jug to fill it. Exactly 4 L remain in the 5 L jug.',
      headline: 'Yes',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 0,
  },
  {
    id: 'wide.067-you-have-12-identical-looking',
    prompt: 'You have 12 identical-looking coins. Exactly one is counterfeit and may be either heavier or lighter than the others. Using only a balance scale, what is the minimum number of weighings needed to guarantee identifying the odd coin and whether it is heavier or lighter?',
    category: 'Logic',
    difficulty: 5,
    reveal: {
      explanation: 'There are 24 possibilities: 12 possible coins × heavy-or-light. A balance scale has three possible outcomes per weighing. Three weighings can theoretically distinguish up to 3³ = 27 outcomes — and a carefully designed strategy can indeed solve all 24 possibilities.',
      headline: '3 weighings',
    },
    type: 'numeric',
    answer: 3,
    displayAnswer: '3 weighings',
    unit: 'weighings',
    magnitude: 'small',
  },
  {
    id: 'wide.068-party-100-people-each-shake',
    prompt: 'At a party, 100 people each shake hands with every other person exactly once. How many handshakes occur?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'You don\'t count 100 × 99, because that counts every handshake twice. The number of unique pairs is: 100 × 99 ÷ 2 = 4,950 The important step is recognizing you\'re counting pairs, not people.',
      headline: '4,950',
    },
    type: 'numeric',
    answer: 4950,
    displayAnswer: '4,950',
    magnitude: 'thousands',
  },
  {
    id: 'wide.069-single-elimination-tournament-starts-with',
    prompt: 'A single-elimination tournament starts with 128 players. No draws, no byes. How many matches must be played to crown one champion?',
    category: 'Logic',
    difficulty: 2,
    reveal: {
      explanation: 'Don\'t build the tournament bracket. Each match eliminates exactly one person. To reduce 128 players to one champion, exactly 127 players must be eliminated.',
      headline: '127 matches',
    },
    type: 'numeric',
    answer: 127,
    displayAnswer: '127 matches',
    unit: 'matches',
    magnitude: 'small',
  },
  {
    id: 'wide.070-rope-wrapped-perfectly-tightly-around',
    prompt: 'A rope is wrapped perfectly tightly around Earth\'s equator. You add exactly 1 metre to the rope and somehow raise it evenly all the way around Earth. Roughly how high above the ground would the rope now float?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'Astonishingly, Earth\'s size doesn\'t matter. Since circumference is 2πr, adding 1 metre increases the radius by: 1 ÷ 2π ≈ 0.159 m So the rope would float roughly 16 cm above the ground everywhere.',
      headline: 'About 16 cm',
    },
    type: 'numeric',
    answer: 16,
    displayAnswer: 'About 16 cm',
    unit: 'cm',
    magnitude: 'small',
  },
  {
    id: 'wide.071-population-bacteria-doubles-every-minute',
    prompt: 'A population of bacteria doubles every minute. A container is completely full at exactly 60 minutes. At what time was it half full?',
    category: 'Logic',
    difficulty: 2,
    reveal: {
      explanation: 'If it doubles every minute, then one minute before it is completely full, it must be exactly half full. The deceptive part is how our intuition treats exponential growth as though it were linear.',
      headline: '59 minutes',
    },
    type: 'numeric',
    answer: 59,
    displayAnswer: '59 minutes',
    unit: 'minutes',
    magnitude: 'small',
  },
  {
    id: 'wide.072-take-normal-8-8-chessboard',
    prompt: 'Take a normal 8 × 8 chessboard and remove two diagonally opposite corner squares. Can the remaining 62 squares be completely covered with 31 ordinary dominoes, where every domino covers exactly two neighboring squares?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'Opposite corners of a chessboard are the same color. Removing them leaves 30 squares of one color and 32 of the other. Every domino always covers one black and one white square, so complete tiling is impossible.',
      headline: 'No',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 1,
  },
  {
    id: 'wide.073-factory-makes-widgets-1-defective',
    prompt: 'A factory makes widgets, and 1% are defective. A scanner correctly flags 99% of defective widgets, but also falsely flags 5% of good widgets. If a widget gets flagged, roughly what is the chance it is actually defective?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'Imagine 10,000 widgets: 100 are defective → about 99 flagged correctly 9,900 are good → about 495 falsely flagged So there are about 594 flagged widgets, but only 99 are genuinely defective: 99 ÷ 594 ≈ 16.7% A highly accurate test can still produce mostly false alarms when the thing being tested for is rare.',
      headline: 'About 17%',
    },
    type: 'probability',
    answer: 17,
    displayAnswer: 'About 17%',
  },
  {
    id: 'wide.075-normal-sea-level-atmospheric-pressure',
    prompt: 'At normal sea-level atmospheric pressure, roughly how much downward force does the atmosphere exert on a 1 square metre surface?',
    category: 'Earth',
    difficulty: 4,
    reveal: {
      explanation: 'Standard atmospheric pressure is about 101,000 newtons per square metre. Under Earth\'s gravity, that\'s approximately the weight of a 10,000 kg mass pressing on every square metre. We aren\'t crushed because pressure inside our bodies and surrounding air pushes back.',
      headline: 'Equivalent to the weight of roughly 10 metric tonnes',
    },
    source: {
      citation: 'NASA / NOAA',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 10,
    displayAnswer: 'Equivalent to the weight of roughly 10 metric tonnes',
    unit: 'tonnes',
    magnitude: 'small',
  },
  {
    id: 'wide.076-how-wide-ostrich-eye',
    prompt: 'Roughly how wide is an ostrich\'s eye?',
    category: 'Animals',
    difficulty: 2,
    reveal: {
      explanation: 'An ostrich\'s eye is almost two inches across — the largest eye of any land animal, and bigger than a golf ball.',
      headline: 'About 5 cm / 2 inches',
    },
    source: {
      citation: 'San Diego Zoo',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 5,
    displayAnswer: 'About 5 cm / 2 inches',
    unit: 'cm',
    magnitude: 'small',
  },
  {
    id: 'wide.077-how-many-hearts-does-octopus',
    prompt: 'How many hearts does an octopus have?',
    category: 'Animals',
    difficulty: 1,
    reveal: {
      explanation: 'Two hearts pump blood through the gills, while a third pumps oxygenated blood around the rest of the body.',
      headline: '3',
    },
    source: {
      citation: 'Smithsonian Ocean',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 3,
    displayAnswer: '3',
    magnitude: 'small',
  },
  {
    id: 'wide.078-which-country-larger-area-japan',
    prompt: 'Which country is larger by total area: Japan or Germany?',
    category: 'Geography',
    difficulty: 2,
    reveal: {
      explanation: 'Japan covers roughly 378,000 km², compared with Germany\'s roughly 357,000 km². Japan is about 6% larger.',
      headline: 'Japan',
    },
    source: {
      citation: 'CIA World Factbook data',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Japan',
      },
      {
        label: 'Germany',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.079-how-many-official-time-zones',
    prompt: 'How many official time zones does Russia span?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Russia stretches from UTC+2 all the way to UTC+12. You can cross 11 time zones without leaving the country.',
      headline: '11',
    },
    source: {
      citation: 'Timeanddate geographic time-zone data',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 11,
    displayAnswer: '11',
    magnitude: 'small',
  },
  {
    id: 'wide.080-which-larger-desert-sahara-antarctica',
    prompt: 'Which is the larger desert: the Sahara or Antarctica?',
    category: 'Geography',
    difficulty: 2,
    reveal: {
      explanation: 'A desert is defined by extremely low precipitation, not heat. Antarctica is both the coldest continent and the largest desert on Earth.',
      headline: 'Antarctica',
    },
    source: {
      citation: 'National Geographic',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The Sahara',
      },
      {
        label: 'Antarctica',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.082-mercury-which-lasts-longer-one',
    prompt: 'On Mercury, which lasts longer: one year or one full sunrise-to-sunrise day?',
    category: 'Space',
    difficulty: 3,
    reveal: {
      explanation: 'Mercury orbits the Sun in 88 Earth days, but one full solar day lasts 176 Earth days. A Mercury day lasts two Mercury years.',
      headline: 'The day',
    },
    source: {
      citation: 'NASA',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'One year',
      },
      {
        label: 'One full sunrise-to-sunrise day',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.084-how-long-giraffe-tongue',
    prompt: 'Roughly how long is a giraffe\'s tongue?',
    category: 'Animals',
    difficulty: 2,
    reveal: {
      explanation: 'A giraffe\'s tongue is roughly the length of an adult forearm and is long enough to grab leaves around thorny branches.',
      headline: 'About 45–50 cm / 18–20 inches',
    },
    source: {
      citation: 'Denver Zoo / Giraffe Conservation Foundation',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 47.4,
    displayAnswer: 'About 45–50 cm / 18–20 inches',
    unit: 'cm',
    magnitude: 'small',
  },
  {
    id: 'wide.087-how-much-saliva-does-average',
    prompt: 'Roughly how much saliva does an average person produce every day?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'Your salivary glands quietly produce about a litre every day, though the amount varies considerably between people.',
      headline: 'About 1 litre',
    },
    source: {
      citation: 'National Institute of Dental and Craniofacial Research',
      kind: 'estimated',
    },
    type: 'numeric',
    answer: 1,
    displayAnswer: 'About 1 litre',
    unit: 'litre',
    magnitude: 'small',
  },
  {
    id: 'wide.088-assuming-wisdom-teeth-present-how',
    prompt: 'Assuming all wisdom teeth are present, how many permanent teeth does a typical adult have?',
    category: 'People',
    difficulty: 1,
    reveal: {
      explanation: 'The full adult set runs from tooth #1 to tooth #32, including the four wisdom teeth. Many people have fewer because wisdom teeth are absent or removed.',
      headline: '32',
    },
    source: {
      citation: 'Cleveland Clinic',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 32,
    displayAnswer: '32',
    magnitude: 'small',
  },
  {
    id: 'wide.089-which-weighs-average-adult-human',
    prompt: 'Which weighs more: an average adult human brain or one litre of water?',
    category: 'People',
    difficulty: 2,
    reveal: {
      explanation: 'A litre of water weighs almost exactly 1 kg. An adult brain weighs roughly 1.2–1.4 kg, or about 3 pounds.',
      headline: 'The brain',
    },
    source: {
      citation: 'NIH / NINDS',
      kind: 'measured',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'An average adult human brain',
      },
      {
        label: 'One litre of water',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.090-how-many-countries-share-land',
    prompt: 'How many countries share a land border with China?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'China ties Russia for one of the world\'s largest numbers of land neighbors: 14 sovereign countries.',
      headline: '14',
    },
    source: {
      citation: 'CIA World Factbook geographic data',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 14,
    displayAnswer: '14',
    magnitude: 'small',
  },
  {
    id: 'wide.092-which-city-farther-north-paris',
    prompt: 'Which city is farther north: Paris or Montreal?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Montreal is around 45.5° N, while Paris is close to 48.9° N. Paris is hundreds of kilometres farther north, despite Montreal\'s much colder winters.',
      headline: 'Paris',
    },
    type: 'which-is-closer',
    options: [
      {
        label: 'Paris',
      },
      {
        label: 'Montreal',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.093-which-came-first-founding-baghdad',
    prompt: 'Which came first: the founding of Baghdad or the conventional beginning of the Viking Age?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Baghdad was founded in 762 CE. The famous raid on Lindisfarne in 793 CE is commonly used as the beginning of the Viking Age — roughly 30 years later.',
      headline: 'Baghdad',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The founding of Baghdad',
      },
      {
        label: 'The conventional beginning of the Viking Age',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.094-which-came-first-samurai-japan',
    prompt: 'Which came first: samurai in Japan or the First Crusade?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Samurai began emerging as professional warriors in Japan during the 10th century. The First Crusade wasn\'t called until 1095.',
      headline: 'Samurai',
    },
    source: {
      citation: 'British Museum / historical chronology',
      kind: 'definitional',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Samurai in Japan',
      },
      {
        label: 'The First Crusade',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.095-greek-mythology-how-many-famous',
    prompt: 'In Greek mythology, how many famous Labors did Heracles — Hercules — ultimately have to complete?',
    category: 'Mythology',
    difficulty: 1,
    reveal: {
      explanation: 'He was originally assigned ten, but two were rejected, leading to the famous Twelve Labors of Heracles.',
      headline: '12',
    },
    type: 'numeric',
    answer: 12,
    displayAnswer: '12',
    magnitude: 'small',
  },
  {
    id: 'wide.096-drawer-contains-black-socks-white',
    prompt: 'A drawer contains black socks and white socks. You\'re choosing in complete darkness. What is the minimum number of socks you must take to guarantee that at least two are the same color?',
    category: 'Logic',
    difficulty: 1,
    reveal: {
      explanation: 'The worst case is one black and one white. Whatever color the third sock is, it must match one of them.',
      headline: '3',
    },
    type: 'numeric',
    answer: 3,
    displayAnswer: '3',
    magnitude: 'small',
  },
  {
    id: 'wide.098-clock-takes-exactly-5-seconds',
    prompt: 'A clock takes exactly 5 seconds to strike 6 o\'clock. Assuming equal spacing between strikes, how long does it take to strike 12 o\'clock?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'Six strikes contain only 5 gaps. If those five gaps take five seconds, each gap takes one second. Twelve strikes contain 11 gaps, so they take 11 seconds.',
      headline: '11 seconds',
    },
    type: 'numeric',
    answer: 11,
    displayAnswer: '11 seconds',
    unit: 'seconds',
    magnitude: 'small',
  },
  {
    id: 'wide.099-doctor-tells-you-take-3',
    prompt: 'A doctor tells you to take 3 pills, one every 30 minutes. You take the first pill immediately. How long until you\'ve taken all three?',
    category: 'Logic',
    difficulty: 1,
    reveal: {
      explanation: 'First pill at 0 minutes, second at 30, third at 60. The common instinct is to say 90 minutes because there are three pills, but there are only two waiting periods.',
      headline: '1 hour',
    },
    type: 'numeric',
    answer: 1,
    displayAnswer: '1 hour',
    unit: 'hour',
    magnitude: 'small',
  },
  {
    id: 'wide.102-how-many-compartments-does-cow',
    prompt: 'How many compartments does a cow\'s stomach have?',
    category: 'Animals',
    difficulty: 1,
    reveal: {
      explanation: 'A cow technically has one stomach divided into four compartments: rumen, reticulum, omasum and abomasum.',
      headline: '4',
    },
    type: 'numeric',
    answer: 4,
    displayAnswer: '4',
    magnitude: 'small',
  },
  {
    id: 'wide.104-how-many-hours-per-day',
    prompt: 'Roughly how many hours per day can a koala sleep?',
    category: 'Animals',
    difficulty: 1,
    reveal: {
      explanation: 'Koalas spend most of the day asleep or resting. Their eucalyptus diet is low in usable energy, so conserving energy is essential.',
      headline: 'About 18–22 hours',
    },
    type: 'numeric',
    answer: 19.9,
    displayAnswer: 'About 18–22 hours',
    unit: 'hours',
    magnitude: 'small',
  },
  {
    id: 'wide.105-if-you-completely-lose-fingernail',
    prompt: 'If you completely lose a fingernail, roughly how long can it take to fully grow back?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'Fingernails only grow around 3 mm per month, so replacing an entire nail can take half a year.',
      headline: 'About 6 months',
    },
    type: 'numeric',
    answer: 6,
    displayAnswer: 'About 6 months',
    unit: 'months',
    magnitude: 'small',
  },
  {
    id: 'wide.106-which-grows-faster-your-fingernails',
    prompt: 'Which grows faster: your fingernails or your toenails?',
    category: 'People',
    difficulty: 1,
    reveal: {
      explanation: 'One study measured average fingernail growth at about 3.5 mm/month, versus about 1.6 mm/month for toenails.',
      headline: 'Fingernails — roughly twice as fast',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Your fingernails',
      },
      {
        label: 'Your toenails',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.107-you-walk-from-bright-daylight',
    prompt: 'You walk from bright daylight into a very dark cave. Roughly how long can your eyes take to become close to fully dark-adapted?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'Your eyes adjust somewhat within minutes, but the rod cells responsible for very-low-light vision can take around 20–30 minutes to reach maximum sensitivity.',
      headline: 'Around 20–30 minutes',
    },
    type: 'numeric',
    answer: 24.5,
    displayAnswer: 'Around 20–30 minutes',
    unit: 'minutes',
    magnitude: 'small',
  },
  {
    id: 'wide.108-what-percentage-people-left-handed',
    prompt: 'Roughly what percentage of people are left-handed?',
    category: 'People',
    difficulty: 2,
    reveal: {
      explanation: 'A huge meta-analysis involving more than two million people estimated left-handedness at about 10.6% — roughly one person in ten.',
      headline: 'About 10–11%',
    },
    type: 'percentage',
    answer: 10.5,
    displayAnswer: 'About 10–11%',
  },
  {
    id: 'wide.109-when-resting-normally-how-much',
    prompt: 'When resting normally, roughly how much air do you breathe in with a single ordinary breath?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'A normal resting breath moves roughly 400–500 mL of air. That\'s only about a quarter of a standard 2-litre drink bottle.',
      headline: 'About 0.5 litres',
    },
    type: 'numeric',
    answer: 0.5,
    displayAnswer: 'About 0.5 litres',
    unit: 'litres',
    magnitude: 'small',
  },
  {
    id: 'wide.111-when-liquid-water-freezes-into',
    prompt: 'When liquid water freezes into ordinary ice, does it shrink or expand — and by roughly how much?',
    category: 'Science',
    difficulty: 3,
    reveal: {
      explanation: 'Water is unusual: its solid form is less dense than its liquid form. That\'s why frozen pipes can burst — and why ice floats.',
      headline: 'It expands by about 9%',
    },
    type: 'percentage',
    answer: 9,
    displayAnswer: 'It expands by about 9%',
  },
  {
    id: 'wide.112-what-fraction-typical-iceberg-hidden',
    prompt: 'Roughly what fraction of a typical iceberg is hidden underwater?',
    category: 'Nature',
    difficulty: 2,
    reveal: {
      explanation: 'Only roughly a tenth of an iceberg\'s volume sticks above the surface. Hence the expression: “the tip of the iceberg.”',
      headline: 'About 90%',
    },
    type: 'percentage',
    answer: 90,
    displayAnswer: 'About 90%',
  },
  {
    id: 'wide.113-which-larger-area-new-zealand',
    prompt: 'Which is larger by total area: New Zealand or the United Kingdom?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'New Zealand is about 269,000 km², while the UK is about 244,000 km². New Zealand is roughly 10% larger.',
      headline: 'New Zealand',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'New Zealand',
      },
      {
        label: 'The United Kingdom',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.114-according-their-official-island-counts',
    prompt: 'According to their official island counts, which has more islands: Sweden or Indonesia?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Sweden\'s official statistical definition produces about 267,570 islands. Indonesia\'s national geospatial agency lists about 17,380. Island definitions differ between countries, but the gap is still wonderfully counterintuitive.',
      headline: 'Sweden — by a huge margin',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Sweden',
      },
      {
        label: 'Indonesia',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.115-narrowest-point-strait-gibraltar-how',
    prompt: 'At the narrowest point of the Strait of Gibraltar, roughly how far apart are Europe and Africa?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Southern Spain and northern Morocco are so close that the distance across the narrowest part of the strait is only around 14 km.',
      headline: 'About 14 km / 9 miles',
    },
    type: 'numeric',
    answer: 14,
    displayAnswer: 'About 14 km / 9 miles',
    unit: 'km',
    magnitude: 'small',
  },
  {
    id: 'wide.116-which-came-first-genghis-khan',
    prompt: 'Which came first: Genghis Khan receiving the title “Genghis Khan” or Magna Carta?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Temüjin united the Mongol tribes and took the title Genghis Khan in 1206. Magna Carta followed only nine years later, in 1215. These events feel like completely different historical worlds, but they were nearly contemporary.',
      headline: 'Genghis Khan',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Genghis Khan receiving the title “Genghis Khan”',
      },
      {
        label: 'Magna Carta',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.117-which-came-first-gutenberg-printing',
    prompt: 'Which came first: Gutenberg\'s printing revolution or the fall of Constantinople?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Gutenberg\'s printing work was underway around 1450. Constantinople fell to the Ottomans in 1453.',
      headline: 'Printing — just barely',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Gutenberg\'s printing revolution',
      },
      {
        label: 'The fall of Constantinople',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.118-which-older-notre-dame-cathedral',
    prompt: 'Which is older: Notre-Dame Cathedral in Paris or Machu Picchu?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Construction of Notre-Dame began in 1163. Machu Picchu was built in the 15th century, around three centuries later. Medieval Europe predates the famous Inca city by a surprisingly large margin.',
      headline: 'Notre-Dame',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Notre-Dame Cathedral in Paris',
      },
      {
        label: 'Machu Picchu',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.119-which-came-first-black-death',
    prompt: 'Which came first: the Black Death reaching Europe or the formation of the Aztec Empire\'s Triple Alliance?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'The Black Death reached Europe around 1347. The Aztec Triple Alliance formed in 1428, about 80 years later.',
      headline: 'The Black Death',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The Black Death reaching Europe',
      },
      {
        label: 'The formation of the Aztec Empire\'s Triple Alliance',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.121-snail-climbing-out-10-metre',
    prompt: 'A snail is climbing out of a 10-metre well. Every day it climbs 3 metres, but every night it slips back 2 metres. How many days until it escapes?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'It gains one metre after each full day-and-night cycle — but on day 8 it reaches the top and escapes before it can slide backward again.',
      headline: '8 days',
    },
    type: 'numeric',
    answer: 8,
    displayAnswer: '8 days',
    unit: 'days',
    magnitude: 'small',
  },
  {
    id: 'wide.123-roulette-wheel-has-landed-red',
    prompt: 'A roulette wheel has landed on red five times in a row. Assuming each spin is independent, is black now more likely on the next spin than it was before the streak?',
    category: 'Logic',
    difficulty: 2,
    reveal: {
      explanation: 'The wheel doesn\'t remember previous spins. A streak of reds does not make black “due.” Believing otherwise is the classic gambler\'s fallacy.',
      headline: 'No',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 1,
  },
  {
    id: 'wide.125-farmer-has-17-sheep-but',
    prompt: 'A farmer has 17 sheep. All but 9 die. How many sheep remain alive?',
    category: 'Logic',
    difficulty: 1,
    reveal: {
      explanation: '“All but nine” means nine are the exceptions. Nine survive.',
      headline: '9',
    },
    type: 'numeric',
    answer: 9,
    displayAnswer: '9',
    magnitude: 'small',
  },
  {
    id: 'wide.128-how-long-can-sloth-hold',
    prompt: 'Roughly how long can a sloth hold its breath underwater?',
    category: 'Animals',
    difficulty: 3,
    reveal: {
      explanation: 'Sloths can slow their heart rate dramatically while swimming, allowing them to stay underwater for around 40 minutes. They\'re also surprisingly capable swimmers.',
      headline: 'Up to about 40 minutes',
    },
    type: 'numeric',
    answer: 40,
    displayAnswer: 'Up to about 40 minutes',
    unit: 'minutes',
    magnitude: 'small',
  },
  {
    id: 'wide.129-what-shape-goat-pupils',
    prompt: 'What shape are a goat\'s pupils?',
    category: 'Animals',
    difficulty: 1,
    reveal: {
      explanation: 'Goats have bizarre-looking horizontal rectangular pupils, which help give grazing animals an extremely wide field of view.',
      headline: 'Rectangular',
    },
    type: 'multiple-choice',
    options: [
      'Round',
      'Vertical slits',
      'Rectangular',
      'Star-shaped',
    ],
    answer: 2,
  },
  {
    id: 'wide.131-how-many-individual-kernels-average',
    prompt: 'Roughly how many individual kernels are on an average ear of corn?',
    category: 'Food',
    difficulty: 3,
    reveal: {
      explanation: 'A typical ear has around 800 kernels arranged in roughly 16 rows. That\'s about 50 kernels per row.',
      headline: 'About 800',
    },
    type: 'numeric',
    answer: 800,
    displayAnswer: 'About 800',
    magnitude: 'small',
  },
  {
    id: 'wide.132-which-longer-inside-your-body',
    prompt: 'Which is longer inside your body: your small intestine or large intestine?',
    category: 'People',
    difficulty: 1,
    reveal: {
      explanation: 'The names refer mostly to width, not length. The small intestine is around 7 metres / 22 feet long, while the large intestine is only around 1.5–1.8 metres.',
      headline: 'Small intestine',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Your small intestine',
      },
      {
        label: 'Large intestine',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.133-which-weighs-your-skin-your',
    prompt: 'Which weighs more: your skin or your brain?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'An adult brain weighs around 3 lb / 1.4 kg. Your skin weighs roughly 6 lb or more, making it the body\'s heaviest organ.',
      headline: 'Your skin',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Your skin',
      },
      {
        label: 'Your brain',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.134-botanically-speaking-which-one-actually',
    prompt: 'Botanically speaking, which one is actually a berry: a banana or a strawberry?',
    category: 'Nature',
    difficulty: 2,
    reveal: {
      explanation: 'Botany is ridiculous. A banana qualifies as a berry, while a strawberry is an aggregate accessory fruit rather than a true botanical berry.',
      headline: 'Banana',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'A banana',
      },
      {
        label: 'A strawberry',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.135-botanically-speaking-what-peanut-closely',
    prompt: 'Botanically speaking, what is a peanut more closely related to?',
    category: 'Nature',
    difficulty: 1,
    reveal: {
      explanation: 'Despite the name, peanuts aren\'t true nuts. They\'re legumes, in the same broad plant family as peas, beans and lentils.',
      headline: 'Peas',
    },
    type: 'multiple-choice',
    options: [
      'Walnuts',
      'Peas',
      'Coconuts',
      'Acorns',
    ],
    answer: 1,
  },
  {
    id: 'wide.136-which-country-shares-france-longest',
    prompt: 'Which country shares France\'s longest land border?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'French Guiana is legally part of France, so France and Brazil share a border of roughly 730 km in South America — France\'s longest border with any neighboring country.',
      headline: 'Brazil',
    },
    type: 'multiple-choice',
    options: [
      'Spain',
      'Germany',
      'Belgium',
      'Brazil',
    ],
    answer: 3,
  },
  {
    id: 'wide.137-which-country-has-ancient-pyramids',
    prompt: 'Which country has more ancient pyramids: Egypt or Sudan?',
    category: 'Geography',
    difficulty: 2,
    reveal: {
      explanation: 'Sudan contains roughly 230–255 surviving ancient pyramids, largely built by the Kingdom of Kush — more than Egypt. They\'re generally much smaller and steeper than the famous Egyptian pyramids.',
      headline: 'Sudan',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Egypt',
      },
      {
        label: 'Sudan',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.138-how-long-before-columbus-did',
    prompt: 'Roughly how long before Columbus did Norse/Viking explorers reach North America?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Norse explorers reached Newfoundland around 1000 CE. Columbus\'s first voyage came in 1492. Europeans had reached North America roughly five centuries earlier.',
      headline: 'About 500 years earlier',
    },
    type: 'numeric',
    answer: 500,
    displayAnswer: 'About 500 years earlier',
    unit: 'years earlier',
    magnitude: 'small',
  },
  {
    id: 'wide.140-which-came-first-europe-eyeglasses',
    prompt: 'Which came first in Europe: eyeglasses or Gutenberg-style printing?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Early spectacles appeared in northern Italy during the 13th century. Gutenberg\'s famous movable-type printing breakthrough came around the 1450s, well over a century later.',
      headline: 'Eyeglasses',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Eyeglasses',
      },
      {
        label: 'Gutenberg-style printing',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.141-according-odyssey-tradition-how-long',
    prompt: 'According to the Odyssey tradition, roughly how long was Odysseus away from home in total after leaving for the Trojan War?',
    category: 'Mythology',
    difficulty: 3,
    reveal: {
      explanation: 'The Trojan War lasted about 10 years, followed by another 10 years of wandering before Odysseus finally returned to Ithaca.',
      headline: 'About 20 years',
    },
    source: {
      citation: 'Homeric tradition',
      kind: 'measured',
    },
    type: 'numeric',
    answer: 20,
    displayAnswer: 'About 20 years',
    unit: 'years',
    magnitude: 'small',
  },
  {
    id: 'wide.142-how-many-heads-does-cerberus',
    prompt: 'How many heads does Cerberus, the famous guard dog of the Greek underworld, traditionally have?',
    category: 'Mythology',
    difficulty: 1,
    reveal: {
      explanation: 'Cerberus is the monstrous three-headed dog guarding the entrance to Hades so that the dead cannot escape.',
      headline: '3',
    },
    type: 'numeric',
    answer: 3,
    displayAnswer: '3',
    magnitude: 'small',
  },
  {
    id: 'wide.143-what-animals-pull-thor-chariot',
    prompt: 'What animals pull Thor\'s chariot in Norse mythology?',
    category: 'Mythology',
    difficulty: 3,
    reveal: {
      explanation: 'Thor\'s chariot is pulled by two goats, traditionally named Tanngrisnir and Tanngnjóstr.',
      headline: 'Goats',
    },
    type: 'multiple-choice',
    options: [
      'Wolves',
      'Horses',
      'Goats',
      'Bears',
    ],
    answer: 2,
  },
  {
    id: 'wide.144-if-there-13-people-room',
    prompt: 'If there are 13 people in a room, can you guarantee that at least two of them were born in the same month?',
    category: 'Logic',
    difficulty: 1,
    reveal: {
      explanation: 'There are only 12 months. Once you have a 13th person, at least one month must contain two birthdays — regardless of which dates they were born on.',
      headline: 'Yes',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 0,
  },
  {
    id: 'wide.145-woman-has-4-daughters-every',
    prompt: 'A woman has 4 daughters, and every daughter has the same one brother. How many children does she have?',
    category: 'Logic',
    difficulty: 1,
    reveal: {
      explanation: 'The four daughters don\'t each have a different brother. They all share the same brother.',
      headline: '5',
    },
    type: 'numeric',
    answer: 5,
    displayAnswer: '5',
    magnitude: 'small',
  },
  {
    id: 'wide.147-cube-painted-red-six-outside',
    prompt: 'A cube is painted red on all six outside faces, then cut into 27 identical smaller cubes. How many small cubes have paint on exactly two faces?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'They\'re the cubes sitting in the middle of each edge. A cube has 12 edges, and with a 3×3×3 division, each edge contributes one such cube.',
      headline: '12',
    },
    type: 'numeric',
    answer: 12,
    displayAnswer: '12',
    magnitude: 'small',
  },
  {
    id: 'wide.151-which-usually-contains-caffeine-one',
    prompt: 'Which usually contains more caffeine in total: one 1-oz espresso shot or one 8-oz cup of brewed coffee?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Espresso is much stronger per ounce, but a typical shot has around 63 mg, while an 8-oz brewed coffee has around 96 mg.',
      headline: 'The brewed coffee',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'One 1-oz espresso shot',
      },
      {
        label: 'One 8-oz cup of brewed coffee',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.153-which-animal-has-largest-eyes',
    prompt: 'Which animal has the largest eyes ever scientifically studied?',
    category: 'Animals',
    difficulty: 3,
    reveal: {
      explanation: 'A living colossal squid\'s eye can be around 27 cm across — roughly soccer-ball sized.',
      headline: 'Colossal squid',
    },
    type: 'multiple-choice',
    options: [
      'Blue whale',
      'Ostrich',
      'Colossal squid',
      'Elephant',
    ],
    answer: 2,
  },
  {
    id: 'wide.155-kiwi-bird-has-unusual-feature',
    prompt: 'A kiwi bird has an unusual feature almost no other bird has. Where are its nostrils?',
    category: 'Animals',
    difficulty: 3,
    reveal: {
      explanation: 'Kiwis actually sniff out underground prey with nostrils positioned near the end of their long bill.',
      headline: 'Near the tip of its beak',
    },
    type: 'multiple-choice',
    options: [
      'Beside its eyes',
      'At the base of its beak',
      'Near the tip of its beak',
      'Under its tongue',
    ],
    answer: 2,
  },
  {
    id: 'wide.157-which-part-your-eye-normally',
    prompt: 'Which part of your eye normally contains no blood vessels at all?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Blood vessels would interfere with transparency, so the cornea instead receives nutrients largely through diffusion from tears and fluid inside the eye.',
      headline: 'Cornea',
    },
    type: 'multiple-choice',
    options: [
      'Retina',
      'Iris',
      'Cornea',
      'Optic nerve',
    ],
    answer: 2,
  },
  {
    id: 'wide.158-how-many-bones-there-one',
    prompt: 'How many bones are there in one human hand, including the wrist bones?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'That\'s 8 wrist bones, 5 metacarpals and 14 finger bones. Your two hands alone contain 54 bones.',
      headline: '27',
    },
    type: 'numeric',
    answer: 27,
    displayAnswer: '27',
    magnitude: 'small',
  },
  {
    id: 'wide.160-which-country-famously-has-three',
    prompt: 'Which country famously has three different capital cities?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Pretoria is administrative, Cape Town legislative, and Bloemfontein traditionally serves as the judicial capital.',
      headline: 'South Africa',
    },
    type: 'multiple-choice',
    options: [
      'India',
      'South Africa',
      'Switzerland',
      'Brazil',
    ],
    answer: 1,
  },
  {
    id: 'wide.162-despite-name-how-long-did',
    prompt: 'Despite its name, roughly how long did the Hundred Years\' War actually last?',
    category: 'History',
    difficulty: 2,
    reveal: {
      explanation: 'The conventional dates are 1337–1453. It wasn\'t one continuous battle, but a long series of conflicts and truces between England and France.',
      headline: '116 years',
    },
    type: 'numeric',
    answer: 116,
    displayAnswer: '116 years',
    unit: 'years',
    magnitude: 'small',
  },
  {
    id: 'wide.163-which-happened-first-1977',
    prompt: 'Which happened first in 1977?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Star Wars opened on May 25, 1977. France\'s final guillotine execution happened that September. Darth Vader existed in cinemas while France was still technically using the guillotine.',
      headline: 'The original Star Wars opened in cinemas',
    },
    type: 'multiple-choice',
    options: [
      'The original Star Wars opened in cinemas',
      'France carried out its final execution by guillotine',
    ],
    answer: 0,
  },
  {
    id: 'wide.164-alexander-great-lived-closer-time',
    prompt: 'Alexander the Great lived closer in time to which event?',
    category: 'Science',
    difficulty: 5,
    reveal: {
      explanation: 'Alexander died in 323 BCE. The Great Pyramid was completed around 2560 BCE, while Apollo 11 landed in 1969 CE. The two gaps are surprisingly close, but the pyramid wins by only around half a century.',
      headline: 'Construction of the Great Pyramid of Giza',
    },
    type: 'multiple-choice',
    options: [
      'Construction of the Great Pyramid of Giza',
      'The first Moon landing',
    ],
    answer: 0,
  },
  {
    id: 'wide.165-which-older-chess-notre-dame',
    prompt: 'Which is older: chess or Notre-Dame Cathedral in Paris?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Chess developed from earlier Indian games and had spread into Persia by around the 6th century CE. Construction of Notre-Dame began in 1163.',
      headline: 'Chess',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Chess',
      },
      {
        label: 'Notre-Dame Cathedral in Paris',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.166-which-came-first-food-canning',
    prompt: 'Which came first: food canning or the bicycle?',
    category: 'Food',
    difficulty: 3,
    reveal: {
      explanation: 'Major breakthroughs in canning came in 1810. Karl Drais demonstrated the famous two-wheeled ancestor of the modern bicycle in 1817. Canned food beat the bicycle by about seven years.',
      headline: 'Food canning',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Food canning',
      },
      {
        label: 'The bicycle',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.169-you-have-6-eggs-you',
    prompt: 'You have 6 eggs. You break 2, fry 2, and eat 2. How many eggs are left?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'The same two eggs can be the ones you break, fry and eat. Only two eggs were actually used.',
      headline: '4',
    },
    type: 'numeric',
    answer: 4,
    displayAnswer: '4',
    magnitude: 'small',
  },
  {
    id: 'wide.201-which-has-potassium-per-100',
    prompt: 'Which has more potassium per 100 grams: banana or avocado?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Avocado has about 485 mg of potassium per 100 g, while banana has about 358 mg. Bananas have the reputation; avocados quietly win.',
      headline: 'Avocado',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Banana',
      },
      {
        label: 'Avocado',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.202-botanically-speaking-what-coffee-bean',
    prompt: 'Botanically speaking, what is a coffee “bean”?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'Coffee beans are actually the seeds inside coffee fruit, commonly called coffee cherries.',
      headline: 'A seed',
    },
    type: 'multiple-choice',
    options: [
      'A nut',
      'A seed',
      'A root',
      'A tiny bean',
    ],
    answer: 1,
  },
  {
    id: 'wide.203-vanilla-comes-from-what-kind',
    prompt: 'Vanilla comes from what kind of plant?',
    category: 'Nature',
    difficulty: 2,
    reveal: {
      explanation: 'Real vanilla comes from the fruit pods of Vanilla planifolia, an orchid. Those little dark flecks in vanilla ice cream are orchid seeds.',
      headline: 'An orchid',
    },
    type: 'multiple-choice',
    options: [
      'A tree',
      'A grass',
      'An orchid',
      'A cactus',
    ],
    answer: 2,
  },
  {
    id: 'wide.204-how-many-those-little-seeds',
    prompt: 'Roughly how many of those little “seeds” are visible on the outside of a typical strawberry?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'A medium strawberry has roughly 200 achenes. Even stranger: those specks aren\'t technically the seeds — each one is actually a tiny fruit containing a seed.',
      headline: 'About 200',
    },
    type: 'numeric',
    answer: 200,
    displayAnswer: 'About 200',
    magnitude: 'small',
  },
  {
    id: 'wide.205-who-has-bones-newborn-baby',
    prompt: 'Who has more bones: a newborn baby or an adult?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Babies are born with roughly 270 bones, while most adults have around 206. Many fuse together as you grow.',
      headline: 'A newborn baby',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'A newborn baby',
      },
      {
        label: 'An adult',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.206-your-brain-2-your-body',
    prompt: 'Your brain is only about 2% of your body weight. Roughly what percentage of your body\'s energy does it use while you\'re resting?',
    category: 'Science',
    difficulty: 3,
    reveal: {
      explanation: 'That little 1.4 kg lump is incredibly expensive to run: the resting brain uses around one-fifth of the body\'s energy supply.',
      headline: 'About 20%',
    },
    type: 'percentage',
    answer: 20,
    displayAnswer: 'About 20%',
  },
  {
    id: 'wide.207-how-many-eyelids-does-camel',
    prompt: 'How many eyelids does a camel have on each eye?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Camels have the usual eyelids plus a third translucent eyelid that can sweep across the eye like a windshield wiper during blowing sand.',
      headline: '3',
    },
    type: 'numeric',
    answer: 3,
    displayAnswer: '3',
    magnitude: 'small',
  },
  {
    id: 'wide.209-what-part-octopus-can-effectively',
    prompt: 'What part of an octopus can effectively taste things by touching them?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Octopus suckers contain specialized chemical receptors, letting their arms effectively taste whatever they touch.',
      headline: 'Its suckers',
    },
    type: 'multiple-choice',
    options: [
      'Its eyes',
      'Its suckers',
      'Its beak',
      'Its skin',
    ],
    answer: 1,
  },
  {
    id: 'wide.211-which-familiar-mammal-has-males',
    prompt: 'Which familiar mammal has males equipped with venomous ankle spurs?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Male platypuses have sharp spurs on their hind feet connected to venom glands. As if egg-laying and a duck bill weren\'t weird enough.',
      headline: 'Platypus',
    },
    type: 'multiple-choice',
    options: [
      'Beaver',
      'Platypus',
      'Otter',
      'Badger',
    ],
    answer: 1,
  },
  {
    id: 'wide.213-how-wide-can-wingspan-wandering',
    prompt: 'Roughly how wide can the wingspan of a wandering albatross reach?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'That\'s wider than many cars are long. The wandering albatross has the largest wingspan of any living bird.',
      headline: 'About 3.5 metres / 11.5 feet',
    },
    type: 'numeric',
    answer: 3.5,
    displayAnswer: 'About 3.5 metres / 11.5 feet',
    unit: 'metres',
    magnitude: 'small',
  },
  {
    id: 'wide.214-which-has-lakes-larger-0',
    prompt: 'Which has more lakes larger than 0.1 km²: Canada or the rest of the world combined?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Canada has more than 875,000 lakes above that size threshold — about 62% of the world\'s total by number.',
      headline: 'Canada',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Canada',
      },
      {
        label: 'The rest of the world combined',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.215-what-world-largest-landlocked-country',
    prompt: 'What is the world\'s largest landlocked country?',
    category: 'Geography',
    difficulty: 2,
    reveal: {
      explanation: 'Kazakhstan is enormous — the ninth-largest country on Earth, despite having no access to an open ocean.',
      headline: 'Kazakhstan',
    },
    type: 'multiple-choice',
    options: [
      'Mongolia',
      'Kazakhstan',
      'Bolivia',
      'Ethiopia',
    ],
    answer: 1,
  },
  {
    id: 'wide.216-which-country-unusual-because-every',
    prompt: 'Which country is unusual because every single point in the country is more than 1,000 metres above sea level?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Lesotho\'s lowest point is still about 1,400 metres above sea level, giving it the highest lowest point of any country.',
      headline: 'Lesotho',
    },
    type: 'multiple-choice',
    options: [
      'Nepal',
      'Switzerland',
      'Lesotho',
      'Bhutan',
    ],
    answer: 2,
  },
  {
    id: 'wide.217-which-came-first-early-fax',
    prompt: 'Which came first: an early fax machine or the abolition of the samurai class in Japan?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Alexander Bain patented an early fax-like device in 1843. Samurai status wasn\'t finally abolished until the 1870s. Fax technology and samurai genuinely overlapped.',
      headline: 'The fax machine',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'An early fax machine',
      },
      {
        label: 'The abolition of the samurai class in Japan',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.218-when-wright-brothers-made-first',
    prompt: 'When the Wright brothers made the first successful powered airplane flight in 1903, which of these still existed?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The Wright Flyer flew in 1903. The Ottoman Empire survived until 1922 — almost another two decades.',
      headline: 'The Ottoman Empire',
    },
    type: 'multiple-choice',
    options: [
      'The Aztec Empire',
      'The Byzantine Empire',
      'The Ottoman Empire',
      'The Roman Empire in the West',
    ],
    answer: 2,
  },
  {
    id: 'wide.219-which-came-first-eiffel-tower',
    prompt: 'Which came first: the Eiffel Tower or commercially sliced bread?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The Eiffel Tower was completed in 1889. Factory-sliced bread didn\'t arrive until 1928 — nearly 40 years later.',
      headline: 'The Eiffel Tower',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The Eiffel Tower',
      },
      {
        label: 'Commercially sliced bread',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.220-which-came-first-london-underground',
    prompt: 'Which came first: the London Underground or the modern automobile?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'London\'s first Underground railway opened in 1863. Benz\'s pioneering automobile arrived in 1885–86. People were riding underground trains before cars existed.',
      headline: 'The London Underground',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The London Underground',
      },
      {
        label: 'The modern automobile',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.221-which-came-first-first-surviving',
    prompt: 'Which came first: the first surviving photograph or the invention of the word “dinosaur”?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Niépce\'s earliest surviving photograph dates to about 1826–27. Richard Owen coined Dinosauria in 1842. Photography existed before dinosaurs had a collective name.',
      headline: 'The photograph',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The first surviving photograph',
      },
      {
        label: 'The invention of the word “dinosaur”',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.224-farm-pen-contains-chickens-rabbits',
    prompt: 'A farm pen contains only chickens and rabbits. There are 10 heads and 28 legs in total. How many rabbits are there?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'If all 10 animals were chickens, you\'d have 20 legs. The extra 8 legs must come from rabbits, which contribute two extra legs each. 8 ÷ 2 = 4 rabbits. This is math, but the nice part is realizing you can solve it without algebra.',
      headline: '4 rabbits',
    },
    type: 'numeric',
    answer: 4,
    displayAnswer: '4 rabbits',
    unit: 'rabbits',
    magnitude: 'small',
  },
  {
    id: 'wide.225-how-many-teeth-can-great',
    prompt: 'Roughly how many teeth can a great white shark have in its mouth at once?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Great whites can carry around 300 teeth arranged in several rows — and continuously replace them as they\'re lost.',
      headline: 'About 300',
    },
    type: 'numeric',
    answer: 300,
    displayAnswer: 'About 300',
    magnitude: 'small',
  },
  {
    id: 'wide.227-what-percentage-netherlands-lies-below',
    prompt: 'Roughly what percentage of the Netherlands lies below sea level?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'About a quarter of the country lies below sea level — much of it land reclaimed and kept dry through an enormous system of dikes, dunes, pumps and water management.',
      headline: 'About 25%',
    },
    type: 'percentage',
    answer: 25,
    displayAnswer: 'About 25%',
  },
  {
    id: 'wide.228-ships-passing-through-panama-canal',
    prompt: 'Ships passing through the Panama Canal don\'t stay at sea level. Roughly how high above sea level are they lifted?',
    category: 'Technology',
    difficulty: 3,
    reveal: {
      explanation: 'The locks work like giant water elevators, raising ships about 26 metres to the level of Gatun Lake before lowering them again on the other side.',
      headline: 'About 26 metres / 85 feet',
    },
    type: 'numeric',
    answer: 26,
    displayAnswer: 'About 26 metres / 85 feet',
    unit: 'metres',
    magnitude: 'small',
  },
  {
    id: 'wide.229-which-sovereign-country-geographically-closer',
    prompt: 'Which sovereign country is geographically closer to Canada?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'The French territory of Saint-Pierre-et-Miquelon sits only about 25 km off Newfoundland. France is therefore much closer to Canada than Mexico is.',
      headline: 'France',
    },
    type: 'which-is-closer',
    options: [
      {
        label: 'France',
      },
      {
        label: 'Mexico',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.230-which-city-sits-higher-above',
    prompt: 'Which city sits higher above sea level?',
    category: 'Geography',
    difficulty: 2,
    reveal: {
      explanation: 'Denver\'s famous “mile high” elevation is about 1,609 m. Mexico City sits around 2,240 m — over 600 metres higher.',
      headline: 'Mexico City',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Denver',
      },
      {
        label: 'Mexico City',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.231-which-country-slightly-larger-surface',
    prompt: 'Which country is slightly larger by surface area?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'They\'re surprisingly close. World Bank figures put Argentina around 2.78 million km² and Kazakhstan around 2.72 million km².',
      headline: 'Argentina',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Argentina',
      },
      {
        label: 'Kazakhstan',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.232-you-measurably-taller-immediately-after',
    prompt: 'Are you measurably taller immediately after waking up than before going to bed — and by roughly how much?',
    category: 'People',
    difficulty: 2,
    reveal: {
      explanation: 'Your spine compresses while you\'re upright and expands again during sleep. Studies have measured daily height changes approaching 2 cm.',
      headline: 'Yes — roughly 1–2 cm',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 0,
  },
  {
    id: 'wide.233-without-using-tricks-like-grouping',
    prompt: 'Without using tricks like grouping information into larger patterns, roughly how many separate “chunks” can your working memory hold at once?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'The old famous claim was “7 ± 2,” but later research suggests the core capacity is closer to 3–5 meaningful chunks, often summarized as about four.',
      headline: 'About 4',
    },
    type: 'numeric',
    answer: 4,
    displayAnswer: 'About 4',
    magnitude: 'small',
  },
  {
    id: 'wide.234-how-quickly-can-person-react',
    prompt: 'Roughly how quickly can a person react to a simple visual event, like seeing a light and pressing a button?',
    category: 'People',
    difficulty: 3,
    reveal: {
      explanation: 'About a quarter of a second is a useful benchmark for simple visual reaction time, though it varies with the task and person.',
      headline: 'Around 250 milliseconds',
    },
    type: 'numeric',
    answer: 250,
    displayAnswer: 'Around 250 milliseconds',
    unit: 'milliseconds',
    magnitude: 'small',
  },
  {
    id: 'wide.235-how-much-would-1-million',
    prompt: 'Roughly how much would $1 million in US $100 bills weigh?',
    category: 'Money',
    difficulty: 3,
    reveal: {
      explanation: 'Every US banknote weighs about 1 gram, regardless of denomination. One million dollars in hundreds is 10,000 notes → roughly 10 kg. This is a good calculator-assisted question because knowing the surprising 1 gram per bill anchor is the actual challenge.',
      headline: 'About 10 kg / 22 lb',
    },
    type: 'numeric',
    answer: 10,
    displayAnswer: 'About 10 kg / 22 lb',
    unit: 'kg',
    magnitude: 'small',
  },
  {
    id: 'wide.236-glass-ice-water-sits-table',
    prompt: 'A glass of ice water sits on a table and eventually becomes wet on the outside. Where did that water come from?',
    category: 'Science',
    difficulty: 1,
    reveal: {
      explanation: 'Water vapor already present in the air cools against the glass and condenses into liquid droplets.',
      headline: 'It came from the surrounding air',
    },
    type: 'multiple-choice',
    options: [
      'It leaked through the glass',
      'It came from the surrounding air',
      'The glass itself released water',
      'The ice pushed water through microscopic holes',
    ],
    answer: 1,
  },
  {
    id: 'wide.237-potato-chip-bags-deliberately-filled',
    prompt: 'Potato-chip bags are deliberately filled with which gas to help keep the chips fresh?',
    category: 'Food',
    difficulty: 2,
    reveal: {
      explanation: 'Nitrogen flushing displaces oxygen, slowing oxidation and helping preserve food. The extra gas also gives fragile chips some physical protection.',
      headline: 'Nitrogen',
    },
    type: 'multiple-choice',
    options: [
      'Oxygen',
      'Nitrogen',
      'Hydrogen',
      'Carbon dioxide',
    ],
    answer: 1,
  },
  {
    id: 'wide.238-what-actually-makes-popcorn-kernel',
    prompt: 'What actually makes a popcorn kernel explode into popcorn?',
    category: 'Food',
    difficulty: 1,
    reveal: {
      explanation: 'Popcorn kernels contain roughly 14% moisture when they\'re ideal for popping. Heating turns that internal water into pressurized steam until the hull ruptures.',
      headline: 'Water inside it turns to steam',
    },
    type: 'multiple-choice',
    options: [
      'Air trapped inside it',
      'Water inside it turns to steam',
      'The starch catches fire',
      'Oil trapped in the shell boils',
    ],
    answer: 1,
  },
  {
    id: 'wide.239-gram-gram-which-usually-contains',
    prompt: 'Gram for gram, which usually contains more sugar?',
    category: 'Food',
    difficulty: 2,
    reveal: {
      explanation: 'USDA legacy data puts ketchup around 21 g of sugar per 100 g, while regular cola is around 10 g per 100 g — roughly half as much by weight. Of course, people normally consume much more cola at once.',
      headline: 'Ketchup',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Ketchup',
      },
      {
        label: 'Regular Cola',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.240-why-does-spreading-salt-icy',
    prompt: 'Why does spreading salt on an icy road help melt the ice?',
    category: 'Science',
    difficulty: 2,
    reveal: {
      explanation: 'Dissolved salt makes it possible for water to remain liquid below its normal 0°C freezing point.',
      headline: 'Salt lowers water\'s freezing point',
    },
    type: 'multiple-choice',
    options: [
      'Salt chemically heats the road',
      'Salt lowers water\'s freezing point',
      'Salt absorbs all the water',
      'Salt reflects more sunlight',
    ],
    answer: 1,
  },
  {
    id: 'wide.241-you-re-driving-hour-then',
    prompt: 'You\'re driving for an hour and then check your tire pressure. It\'s somewhat higher than when the tires were cold. What\'s the main reason?',
    category: 'Science',
    difficulty: 2,
    reveal: {
      explanation: 'Driving flexes and heats the tires. Hotter gas produces greater pressure inside the same tire even though roughly the same amount of air is still there.',
      headline: 'The air inside became hotter',
    },
    type: 'multiple-choice',
    options: [
      'The tires sucked in extra air',
      'The air inside became hotter',
      'Centrifugal force created more air',
      'The rubber chemically produced gas',
    ],
    answer: 1,
  },
  {
    id: 'wide.243-earliest-commercial-microwave-ovens-enormous',
    prompt: 'The earliest commercial microwave ovens were enormous. Roughly how much did one of the first models weigh?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'The first Radarange models were about six feet tall and 750 pounds. The microwave began as industrial equipment, not a little kitchen box.',
      headline: 'About 340 kg / 750 lb',
    },
    type: 'numeric',
    answer: 340,
    displayAnswer: 'About 340 kg / 750 lb',
    unit: 'kg',
    magnitude: 'small',
  },
  {
    id: 'wide.244-discovery-led-percy-spencer-toward',
    prompt: 'The discovery that led Percy Spencer toward the microwave oven began when something unexpectedly melted near radar equipment. What was it?',
    category: 'History',
    difficulty: 2,
    reveal: {
      explanation: 'Spencer noticed a candy bar in his pocket melting near a magnetron in 1945, then deliberately experimented with popcorn and an egg.',
      headline: 'A candy/chocolate bar',
    },
    type: 'multiple-choice',
    options: [
      'A plastic pen',
      'A candy/chocolate bar',
      'His watch strap',
      'A block of cheese',
    ],
    answer: 1,
  },
  {
    id: 'wide.245-which-existed-first-commercially-automatic',
    prompt: 'Which existed first commercially?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'The automatic toaster debuted in 1926. The first effective machine-sliced bread went on sale in 1928. So sliced bread wasn\'t even the best thing since the pop-up toaster — the toaster came first.',
      headline: 'The automatic pop-up toaster',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'The Automatic Pop-Up Toaster',
      },
      {
        label: 'Machine-Sliced Bread',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.246-one-earliest-known-vending-machine',
    prompt: 'One of the earliest known vending-machine designs was described nearly 2,000 years ago by Hero of Alexandria. What did it dispense when someone inserted a coin?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'A coin landed on a lever, briefly opening a valve and dispensing a measured amount of water. Ancient engineering got surprisingly sophisticated.',
      headline: 'Holy water',
    },
    type: 'multiple-choice',
    options: [
      'Wine',
      'Bread',
      'Holy water',
      'Theater tickets',
    ],
    answer: 2,
  },
  {
    id: 'wide.247-flat-round-pizza-must-stay',
    prompt: 'A flat round pizza must stay in place, and every cut is a straight line across it. What is the maximum number of pieces you can make with exactly 3 cuts?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'To maximize the pieces, every new cut must cross every previous cut at a different point: 1 cut → 2 2 cuts → 4 3 cuts → 7 Eight would require stacking/rearranging pieces or cutting through the pizza in 3D.',
      headline: '7',
    },
    type: 'numeric',
    answer: 7,
    displayAnswer: '7',
    magnitude: 'small',
  },
  {
    id: 'wide.248-month-with-31-days-how',
    prompt: 'In a month with 31 days, how many different weekdays must occur five times?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'Four complete weeks account for 28 days. The remaining three days mean three consecutive weekdays appear a fifth time.',
      headline: '3 weekdays',
    },
    type: 'numeric',
    answer: 3,
    displayAnswer: '3 weekdays',
    unit: 'weekdays',
    magnitude: 'small',
  },
  {
    id: 'wide.250-if-you-take-10-minute',
    prompt: 'If you take a 10-minute shower using a conventional showerhead, roughly how much water can flow out?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'A conventional showerhead can use about 2.5 gallons per minute. Over ten minutes that\'s roughly 25 gallons, or 95 litres. Efficient WaterSense models cap that at 2.0 gallons per minute.',
      headline: 'Around 95 litres / 25 US gallons',
    },
    type: 'numeric',
    answer: 95,
    displayAnswer: 'Around 95 litres / 25 US gallons',
    unit: 'litres',
    magnitude: 'small',
  },
  {
    id: 'wide.251-airport-runways-have-numbers-like',
    prompt: 'Airport runways have numbers like 09, 18, or 27 painted on them. What do those numbers roughly represent?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Runway numbers correspond to the runway\'s magnetic heading rounded to the nearest 10 degrees. So Runway 27 points roughly 270° — west.',
      headline: 'Compass direction',
    },
    type: 'multiple-choice',
    options: [
      'Runway length',
      'Compass direction',
      'Airport elevation',
      'Construction year',
    ],
    answer: 1,
  },
  {
    id: 'wide.252-why-manhole-covers-so-often',
    prompt: 'Why are manhole covers so often round instead of square?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'A circle has the same width in every direction. A matching round cover can\'t rotate into an orientation that lets it fall through the hole.',
      headline: 'A round cover can\'t fall through its own opening',
    },
    type: 'multiple-choice',
    options: [
      'Circles use less metal',
      'They\'re easier to paint',
      'A round cover can\'t fall through its own opening',
      'Water flows around them better',
    ],
    answer: 2,
  },
  {
    id: 'wide.253-what-color-blood-flowing-through',
    prompt: 'What color is the blood flowing through the blue-looking veins in your hand?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'Human blood is always some shade of red. Veins look blue because of how light passes through and reflects from skin.',
      headline: 'Dark red',
    },
    type: 'multiple-choice',
    options: [
      'Blue',
      'Purple',
      'Dark red',
      'Colorless',
    ],
    answer: 2,
  },
  {
    id: 'wide.254-old-school-diagram-says-tip',
    prompt: 'An old school diagram says the tip of your tongue tastes sweet, while other sections handle sour, salty and bitter. How accurate is that?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Taste receptors for all five basic tastes are distributed across the tongue, although some regions are slightly more sensitive than others.',
      headline: 'Basically a myth',
    },
    type: 'multiple-choice',
    options: [
      'Completely accurate',
      'Mostly accurate',
      'Basically a myth',
    ],
    answer: 2,
  },
  {
    id: 'wide.256-lead-inside-ordinary-pencil-primarily',
    prompt: 'The “lead” inside an ordinary pencil is primarily made from what?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'Pencil “lead” contains graphite, typically mixed with clay or similar material. Actual lead metal isn\'t involved.',
      headline: 'Graphite',
    },
    type: 'multiple-choice',
    options: [
      'Lead',
      'Graphite',
      'Charcoal',
      'Iron',
    ],
    answer: 1,
  },
  {
    id: 'wide.257-when-ancient-greek-marble-statues',
    prompt: 'When ancient Greek marble statues were new, what did many of them look like?',
    category: 'History',
    difficulty: 2,
    reveal: {
      explanation: 'Much ancient Greek and Roman sculpture was originally painted in vivid colors. Most pigment simply disappeared over the centuries, leaving the white marble we\'re familiar with today.',
      headline: 'Brightly painted',
    },
    type: 'multiple-choice',
    options: [
      'Plain white marble',
      'Brightly painted',
      'Covered entirely in gold',
      'Mostly black',
    ],
    answer: 1,
  },
  {
    id: 'wide.258-which-helmet-representative-actual-viking',
    prompt: 'Which helmet is more representative of an actual Viking-age warrior?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'The famous horned Viking helmet is largely a later cultural image. The one substantially preserved Viking-age helmet does not have horns.',
      headline: 'Simple helmet without horns',
    },
    type: 'multiple-choice',
    options: [
      'Helmet with huge horns',
      'Simple helmet without horns',
    ],
    answer: 1,
  },
  {
    id: 'wide.259-standard-roman-numeral-system-which',
    prompt: 'In the standard Roman numeral system, which symbol represents zero?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Standard Roman numerals were designed for counting positive quantities and don\'t have a normal numeral equivalent to our zero.',
      headline: 'There isn\'t one',
    },
    type: 'multiple-choice',
    options: [
      'Z',
      'O',
      'N',
      'There isn\'t one',
    ],
    answer: 3,
  },
  {
    id: 'wide.260-which-existed-first-harvard-university',
    prompt: 'Which existed first?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Harvard was founded in 1636. Newton\'s major early work on what became calculus came in 1665–1666. A university could have enrolled you decades before calculus as we know it existed.',
      headline: 'Harvard University',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Harvard University',
      },
      {
        label: 'Calculus',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.261-which-invented-first-traffic-signal',
    prompt: 'Which was invented first?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'London\'s first traffic signal appeared in 1868, controlling horses, carriages and pedestrians — well before the automobile era. It used semaphore arms and red/green gas lamps.',
      headline: 'The traffic signal',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'A Traffic Signal',
      },
      {
        label: 'The Modern Automobile',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.262-why-does-pressure-cooker-cook',
    prompt: 'Why does a pressure cooker cook food faster?',
    category: 'Food',
    difficulty: 2,
    reveal: {
      explanation: 'Increased pressure raises water\'s boiling temperature, letting the contents cook at temperatures above the normal 100°C sea-level boiling point.',
      headline: 'Pressure lets the water get hotter before boiling',
    },
    type: 'multiple-choice',
    options: [
      'The metal pot gets hotter than other pots',
      'Pressure lets the water get hotter before boiling',
      'Steam contains extra oxygen',
      'The food is physically compressed',
    ],
    answer: 1,
  },
  {
    id: 'wide.263-why-does-drainpipe-under-sink',
    prompt: 'Why does the drainpipe under a sink usually have a U-shaped bend?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The bend traps a small amount of water, creating a simple barrier between your room and the gases in the sewer system.',
      headline: 'It holds water that blocks sewer gases',
    },
    type: 'multiple-choice',
    options: [
      'It slows the water to stop splashing',
      'It catches jewelry',
      'It holds water that blocks sewer gases',
      'It increases water pressure',
    ],
    answer: 2,
  },
  {
    id: 'wide.264-what-ingredient-mainly-responsible-making',
    prompt: 'What ingredient is mainly responsible for making stainless steel resist rust much better than ordinary steel?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Stainless steel contains enough chromium to develop a very thin protective oxide layer on its surface. If scratched, that layer can reform in the presence of oxygen.',
      headline: 'Chromium',
    },
    type: 'multiple-choice',
    options: [
      'Silver',
      'Chromium',
      'Aluminum',
      'Tin',
    ],
    answer: 1,
  },
  {
    id: 'wide.265-where-does-sound-travel-faster',
    prompt: 'Where does sound travel faster?',
    category: 'Earth',
    difficulty: 2,
    reveal: {
      explanation: 'Sound travels around 340 m/s in air but roughly 1,500 m/s in seawater — more than four times faster.',
      headline: 'Water',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Air',
      },
      {
        label: 'Water',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.267-what-actually-triggers-your-eyes',
    prompt: 'What actually triggers your eyes to water while cutting onions?',
    category: 'Earth',
    difficulty: 2,
    reveal: {
      explanation: 'Damaging onion cells starts chemical reactions that release volatile sulfur compounds, including the famous tear-inducing “lachrymatory factor.”',
      headline: 'A volatile sulfur compound released into the air',
    },
    type: 'multiple-choice',
    options: [
      'Onion juice physically splashing into your eyes',
      'A volatile sulfur compound released into the air',
      'The smell psychologically triggers tears',
      'Tiny onion dust particles',
    ],
    answer: 1,
  },
  {
    id: 'wide.268-qr-codes-originally-developed-primarily',
    prompt: 'QR codes were originally developed primarily for what?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Denso developed QR codes for production management in the automotive industry, where ordinary barcodes weren\'t holding enough information or scanning quickly enough.',
      headline: 'Tracking automotive parts during manufacturing',
    },
    type: 'multiple-choice',
    options: [
      'Restaurant menus',
      'Online payments',
      'Tracking automotive parts during manufacturing',
      'Airline boarding passes',
    ],
    answer: 2,
  },
  {
    id: 'wide.269-why-do-modern-passenger-airplanes',
    prompt: 'Why do modern passenger airplanes generally have rounded windows rather than square ones?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Sharp corners concentrate stress in a pressurized fuselage. Investigations of early de Havilland Comet failures showed how dangerous squarish window corners could be.',
      headline: 'Curved corners reduce dangerous stress concentrations',
    },
    type: 'multiple-choice',
    options: [
      'They\'re easier to clean',
      'Curved corners reduce dangerous stress concentrations',
      'They improve aerodynamics',
      'They make the cabin brighter',
    ],
    answer: 1,
  },
  {
    id: 'wide.270-what-color-airplane-famous-black',
    prompt: 'What color is an airplane\'s famous “black box” flight recorder actually painted?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Flight recorders are intentionally painted bright orange or similar high-visibility colors so investigators can find them more easily after a crash.',
      headline: 'Bright orange',
    },
    type: 'multiple-choice',
    options: [
      'Black',
      'White',
      'Bright orange',
      'Silver',
    ],
    answer: 2,
  },
  {
    id: 'wide.271-bubble-wrap-originally-invented-while',
    prompt: 'Bubble Wrap was originally invented while trying to create what product?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Its inventors sealed two plastic sheets together in 1957 while attempting to make a fashionable textured wallpaper. The packaging use came later.',
      headline: 'Textured wallpaper',
    },
    type: 'multiple-choice',
    options: [
      'Life jackets',
      'Textured wallpaper',
      'Greenhouse roofing',
      'Packing material',
    ],
    answer: 1,
  },
  {
    id: 'wide.272-idea-behind-velcro-style-hook',
    prompt: 'The idea behind Velcro-style hook-and-loop fasteners was inspired by what?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'George de Mestral examined burrs that had stuck to his clothes and his dog\'s fur and discovered tiny natural hooks catching on loops.',
      headline: 'Plant burrs sticking to clothing and fur',
    },
    type: 'multiple-choice',
    options: [
      'Spiderwebs',
      'Fish scales',
      'Plant burrs sticking to clothing and fur',
      'Bird feathers',
    ],
    answer: 2,
  },
  {
    id: 'wide.273-adhesive-eventually-led-post-notes',
    prompt: 'The adhesive that eventually led to Post-it Notes was originally considered unusual because it was...',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Spencer Silver was trying to create a strong adhesive but instead produced one that stuck lightly and could peel away cleanly. Another 3M scientist later realized that was exactly what removable notes needed.',
      headline: 'Too weak',
    },
    type: 'multiple-choice',
    options: [
      'Too weak',
      'Too strong',
      'Waterproof',
      'Magnetic',
    ],
    answer: 0,
  },
  {
    id: 'wide.274-which-instrument-family-does-saxophone',
    prompt: 'To which instrument family does the saxophone belong?',
    category: 'Technology',
    difficulty: 3,
    reveal: {
      explanation: 'Classification is based on how the sound is produced. A saxophone uses a vibrating reed, so it\'s a woodwind despite its brass body.',
      headline: 'Woodwind',
    },
    type: 'multiple-choice',
    options: [
      'Brass',
      'Woodwind',
      'Percussion',
      'Strings',
    ],
    answer: 1,
  },
  {
    id: 'wide.275-where-does-english-word-alphabet',
    prompt: 'Where does the English word “alphabet” ultimately get its name?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Alphabet ultimately comes from Greek alpha + beta — essentially the Greek equivalent of saying “the ABCs.”',
      headline: 'From the first two Greek letters',
    },
    type: 'multiple-choice',
    options: [
      'From the first two Greek letters',
      'From a Roman emperor',
      'From the Latin word for writing',
      'From an ancient Egyptian city',
    ],
    answer: 0,
  },
  {
    id: 'wide.276-what-does-zip-zip-code',
    prompt: 'What does the “ZIP” in ZIP Code originally stand for?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The U.S. Postal Service introduced ZIP Codes in 1963 to make rapidly growing mail volumes easier to sort and deliver.',
      headline: 'Zone Improvement Plan',
    },
    type: 'multiple-choice',
    options: [
      'Zonal Identification Post',
      'Zone Improvement Plan',
      'Zip Information Protocol',
      'It doesn\'t stand for anything',
    ],
    answer: 1,
  },
  {
    id: 'wide.277-first-product-ever-scanned-using',
    prompt: 'The first product ever scanned using a modern UPC barcode at a supermarket checkout was what?',
    category: 'Technology',
    difficulty: 3,
    reveal: {
      explanation: 'On June 26, 1974, a 67-cent pack of Juicy Fruit became the first UPC-scanned retail purchase.',
      headline: 'A pack of chewing gum',
    },
    type: 'multiple-choice',
    options: [
      'A can of soup',
      'A pack of chewing gum',
      'A bottle of Coca-Cola',
      'A loaf of bread',
    ],
    answer: 1,
  },
  {
    id: 'wide.278-inventor-supermarket-shopping-cart-got',
    prompt: 'The inventor of the supermarket shopping cart got the basic idea for its frame from what everyday object?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Sylvan Goldman\'s first shopping cart was essentially a folding-chair frame on wheels holding baskets. Customers initially hated the idea, so he hired people to push carts around the store until they caught on.',
      headline: 'Folding chairs',
    },
    type: 'multiple-choice',
    options: [
      'A wheelbarrow',
      'A baby stroller',
      'Folding chairs',
      'A bicycle',
    ],
    answer: 2,
  },
  {
    id: 'wide.279-when-statue-liberty-unveiled-1886',
    prompt: 'When the Statue of Liberty was unveiled in 1886, what color was it?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The outside is copper. Its famous green color formed gradually as the metal oxidized; it took roughly 30 years for the familiar patina to develop fully.',
      headline: 'Coppery brown',
    },
    type: 'multiple-choice',
    options: [
      'Green',
      'Coppery brown',
      'White',
      'Dark gray',
    ],
    answer: 1,
  },
  {
    id: 'wide.280-what-official-name-golden-gate',
    prompt: 'What is the official name of the Golden Gate Bridge\'s distinctive color?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The color was chosen partly because it suited the landscape and helped the enormous bridge remain visible in San Francisco\'s fog.',
      headline: 'International Orange',
    },
    type: 'multiple-choice',
    options: [
      'California Red',
      'Sunset Orange',
      'International Orange',
      'Golden Vermilion',
    ],
    answer: 2,
  },
  {
    id: 'wide.281-what-tiny-pocket-inside-front',
    prompt: 'What was the tiny pocket inside the front pocket of traditional jeans originally intended to hold?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'What we often call the coin pocket today was originally the watch pocket, designed for carrying a small pocket watch securely.',
      headline: 'A pocket watch',
    },
    type: 'multiple-choice',
    options: [
      'Coins',
      'Matches',
      'A pocket watch',
      'A key',
    ],
    answer: 2,
  },
  {
    id: 'wide.282-technology-name-bluetooth-inspired',
    prompt: 'The technology name Bluetooth was inspired by...',
    category: 'Technology',
    difficulty: 3,
    reveal: {
      explanation: 'It\'s named after Harald “Bluetooth” Gormsson, a 10th-century king associated with uniting Denmark and Norway. Engineers liked the analogy of one technology “uniting” different devices.',
      headline: 'A medieval Scandinavian king',
    },
    type: 'multiple-choice',
    options: [
      'A blue communications cable',
      'A medieval Scandinavian king',
      'The inventor\'s nickname',
      'A Navy radio code',
    ],
    answer: 1,
  },
  {
    id: 'wide.283-what-does-sos-officially-stand',
    prompt: 'What does SOS officially stand for?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'SOS wasn\'t created as an abbreviation. The continuous Morse pattern ...---... was selected because it was simple and extremely recognizable. “Save Our Ship” and “Save Our Souls” came later as memory aids.',
      headline: 'Nothing',
    },
    type: 'multiple-choice',
    options: [
      'Save Our Ship',
      'Save Our Souls',
      'Signal Our Safety',
      'Nothing',
    ],
    answer: 3,
  },
  {
    id: 'wide.284-dot-above-lowercase-actually-has',
    prompt: 'The dot above a lowercase i or j actually has a name. What is it?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Yes, really: the tiny dot is called a tittle. The word has existed in English since the Middle Ages.',
      headline: 'Tittle',
    },
    type: 'multiple-choice',
    options: [
      'Tittle',
      'Jot',
      'Serif',
      'Pip',
    ],
    answer: 0,
  },
  {
    id: 'wide.285-german-chocolate-cake-named-after',
    prompt: 'German chocolate cake is named after...',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'American chocolatier Samuel German developed a sweet baking chocolate in the 1800s. “German\'s Chocolate Cake” eventually lost the apostrophe, making the dessert sound German when it isn\'t.',
      headline: 'A baker named German',
    },
    type: 'multiple-choice',
    options: [
      'Germany',
      'A baker named German',
      'A German royal family',
      'A town called German',
    ],
    answer: 1,
  },
  {
    id: 'wide.286-during-typical-high-altitude-passenger',
    prompt: 'During a typical high-altitude passenger flight, the airplane cabin usually feels like you\'re standing at roughly what altitude?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'The cabin isn\'t usually pressurized all the way to sea-level pressure. Traditional airliners commonly maintain pressure equivalent to roughly 8,000 feet at high cruise altitude.',
      headline: 'Around 6,000–8,000 feet / 1,800–2,400 m',
    },
    type: 'numeric',
    answer: 6930,
    displayAnswer: 'Around 6,000–8,000 feet / 1,800–2,400 m',
    unit: 'feet',
    magnitude: 'thousands',
  },
  {
    id: 'wide.287-what-main-purpose-tiny-hole',
    prompt: 'What\'s the main purpose of the tiny hole you may notice near the bottom of an airplane passenger window?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Airplane windows have multiple layers. The small vent hole lets cabin pressure reach the space between panes so the outer pane normally takes the pressure load.',
      headline: 'Equalize pressure between window panes',
    },
    type: 'multiple-choice',
    options: [
      'Drain rainwater',
      'Equalize pressure between window panes',
      'Let fresh air into the cabin',
      'Stop the window fogging by heating it',
    ],
    answer: 1,
  },
  {
    id: 'wide.288-if-microwave-radiation-trapped-inside',
    prompt: 'If microwave radiation is trapped inside a microwave oven, why can you still see through all those little holes in the door?',
    category: 'Technology',
    difficulty: 3,
    reveal: {
      explanation: 'The metal screen\'s holes are tiny compared with the wavelength of the microwaves, so the screen behaves almost like a solid wall to them while much shorter-wavelength visible light gets through.',
      headline: 'The holes are too small for the microwaves but visible light can pass through',
    },
    type: 'multiple-choice',
    options: [
      'The microwaves travel only downward',
      'The holes are too small for the microwaves but visible light can pass through',
      'The glass absorbs the radiation',
      'The microwave shuts off whenever you look through it',
    ],
    answer: 1,
  },
  {
    id: 'wide.290-take-sheet-standard-a4-paper',
    prompt: 'Take a sheet of standard A4 paper and cut it exactly in half across its long side. What is special about the two smaller sheets?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'The A-series uses an aspect ratio of 1 : √2. Halving a sheet produces the next size down while preserving exactly the same shape. That\'s why A4 → A5 → A6 scales so neatly.',
      headline: 'They keep the same proportions as the original sheet',
    },
    type: 'multiple-choice',
    options: [
      'They become square',
      'They keep the same proportions as the original sheet',
      'They\'re shaped like US Letter paper',
      'Nothing special',
    ],
    answer: 1,
  },
  {
    id: 'wide.291-how-many-dot-positions-make',
    prompt: 'How many dot positions make up one standard Braille cell?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Traditional Braille uses a 2 × 3 arrangement of six possible raised dots, with different patterns representing letters, numbers and punctuation.',
      headline: '6',
    },
    type: 'numeric',
    answer: 6,
    displayAnswer: '6',
    magnitude: 'small',
  },
  {
    id: 'wide.292-how-many-keys-does-standard',
    prompt: 'How many keys does a standard modern piano have?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'The modern standard is 52 white keys + 36 black keys = 88. That layout became established by around the 1890s.',
      headline: '88',
    },
    type: 'numeric',
    answer: 88,
    displayAnswer: '88',
    magnitude: 'small',
  },
  {
    id: 'wide.293-researchers-asked-hospital-staff-whether',
    prompt: 'Researchers asked hospital staff whether they\'d ever felt their phone or pager vibrating when it actually wasn\'t. Roughly what percentage said yes?',
    category: 'Technology',
    difficulty: 3,
    reveal: {
      explanation: 'In one study, 115 of 169 respondents — 68% — reported phantom vibrations. Your brain can apparently become so accustomed to expecting notifications that it invents them.',
      headline: 'About 68%',
    },
    type: 'percentage',
    answer: 68,
    displayAnswer: 'About 68%',
  },
  {
    id: 'wide.294-around-year-1600-england-which',
    prompt: 'Around the year 1600 in England, which of these would have still been relatively unusual at the dinner table?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Europeans had known forks for centuries, but their widespread acceptance as personal eating utensils came surprisingly late. They only really caught on in England during the 17th century.',
      headline: 'A fork',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'A Fork',
      },
      {
        label: 'A Spoon',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.295-large-air-conditioned-building-which',
    prompt: 'For a large air-conditioned building, which entrance generally does a better job of preventing outside air from rushing in?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'A revolving door never creates one large unobstructed opening between indoors and outdoors, reducing unwanted air exchange. Energy-efficiency guidance for large buildings specifically recommends them as one way to reduce infiltration.',
      headline: 'Revolving door',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'Revolving Door',
      },
      {
        label: 'Normal Swinging Door',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.296-which-banknote-expected-survive-longer',
    prompt: 'Which U.S. banknote is expected to survive longer in circulation?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Current Federal Reserve estimates put a $1 bill at about 7.2 years, versus roughly 24 years for a $100 bill. Hundreds tend to be handled less frequently.',
      headline: '$100 bill — by a lot',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: '$1 Bill',
      },
      {
        label: '$100 Bill',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.297-will-year-2100-be-leap',
    prompt: 'Will the year 2100 be a leap year?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Being divisible by 4 isn\'t enough for century years. Years divisible by 100 are not leap years unless they\'re also divisible by 400. So: 2000 ✅ 2100 ❌ 2400 ✅',
      headline: 'No',
    },
    type: 'multiple-choice',
    options: [
      'Yes',
      'No',
    ],
    answer: 1,
  },
  {
    id: 'wide.298-standard-double-six-domino-set',
    prompt: 'A standard double-six domino set contains every possible pair from 0–0 through 6–6. How many tiles are in the whole set?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'There are seven possible values on each half, including blank. Counting every unique unordered pairing gives 28 dominoes. It\'s possible to reason toward this without knowing anything about dominoes.',
      headline: '28',
    },
    type: 'numeric',
    answer: 28,
    displayAnswer: '28',
    magnitude: 'small',
  },
  {
    id: 'wide.300-which-one-normally-begins-reading',
    prompt: 'Which one normally begins reading information closer to the center and progresses outward?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Traditional vinyl records normally start near the outer edge and spiral inward. CDs reverse that basic direction: their data track begins near the inside and progresses toward the outside.',
      headline: 'A compact disc',
    },
    type: 'which-is-closer',
    options: [
      {
        label: 'A Vinyl Record',
      },
      {
        label: 'A Compact Disc',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.301-what-world-first-webcam-originally',
    prompt: 'What was the world\'s first webcam originally pointed at?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Cambridge researchers set up a camera so they could check whether the shared Trojan Room coffee pot had fresh coffee without walking over to it. It later became the first webcam connected to the Web.',
      headline: 'A coffee pot',
    },
    type: 'multiple-choice',
    options: [
      'A parking lot',
      'A coffee pot',
      'A laboratory entrance',
      'A weather station',
    ],
    answer: 1,
  },
  {
    id: 'wide.302-which-longer-standard-marathon-shortest',
    prompt: 'Which is longer?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'A marathon is 42.195 km. Britain and France are only about 33 km apart at the narrowest part of the Strait of Dover.',
      headline: 'The marathon',
    },
    type: 'which-is-bigger',
    options: [
      {
        label: 'A standard marathon',
      },
      {
        label: 'The shortest distance across the English Channel',
      },
    ],
    answer: 0,
  },
  {
    id: 'wide.303-which-city-caesar-salad-generally',
    prompt: 'In which city is the Caesar salad generally credited with being created?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The standard origin story places its creation at Caesar Cardini\'s restaurant in Tijuana, Mexico, in 1924.',
      headline: 'Tijuana',
    },
    type: 'multiple-choice',
    options: [
      'Rome',
      'New York',
      'Tijuana',
      'Paris',
    ],
    answer: 2,
  },
  {
    id: 'wide.304-reinforced-concrete-what-steel-reinforcement',
    prompt: 'In reinforced concrete, what is the steel reinforcement mainly there to handle?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Concrete is excellent under compression but relatively poor under tension. Steel complements it by taking tensile forces.',
      headline: 'Tension',
    },
    type: 'multiple-choice',
    options: [
      'Compression',
      'Tension',
      'Heat',
      'Water absorption',
    ],
    answer: 1,
  },
  {
    id: 'wide.305-what-did-first-sms-text',
    prompt: 'What did the first SMS text message say?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'The first SMS was sent over Vodafone\'s network on December 3, 1992 and simply read “Merry Christmas.”',
      headline: 'Merry Christmas',
    },
    type: 'multiple-choice',
    options: [
      'Hello World',
      'Test 123',
      'Merry Christmas',
      'Call me',
    ],
    answer: 2,
  },
  {
    id: 'wide.306-how-many-countries-share-land',
    prompt: 'How many countries share a land border with Germany?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Germany borders Denmark, Poland, Czechia, Austria, Switzerland, France, Luxembourg, Belgium and the Netherlands.',
      headline: '9',
    },
    type: 'numeric',
    answer: 9,
    displayAnswer: '9',
    magnitude: 'small',
  },
  {
    id: 'wide.307-what-causes-burning-sensation-from',
    prompt: 'What causes the burning sensation from a hot chili pepper?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Capsaicin activates TRPV1, a sensory receptor also activated by damaging heat. Your food isn\'t literally heating your mouth to burning temperature; your nervous system is receiving a similar warning signal.',
      headline: 'A chemical activating heat/pain-sensitive receptors',
    },
    type: 'multiple-choice',
    options: [
      'A chemical activating heat/pain-sensitive receptors',
      'Microscopic burns to your tongue',
      'An acidic reaction with saliva',
      'Taste buds becoming overheated',
    ],
    answer: 0,
  },
  {
    id: 'wide.308-what-main-reason-train-wheel',
    prompt: 'What is the main reason train wheel surfaces are made slightly conical rather than perfectly cylindrical?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'The conical shape lets the effective wheel diameter change slightly as the axle shifts sideways, helping a wheelset self-center and travel around curves without excessive slipping.',
      headline: 'To help the wheels stay centered and negotiate curves',
    },
    type: 'multiple-choice',
    options: [
      'To reduce the train\'s weight',
      'To help the wheels stay centered and negotiate curves',
      'To make braking easier',
      'To reduce noise',
    ],
    answer: 1,
  },
  {
    id: 'wide.309-which-country-hawaiian-pizza-created',
    prompt: 'In which country was Hawaiian pizza created?',
    category: 'Geography',
    difficulty: 2,
    reveal: {
      explanation: 'Greek-Canadian restaurateur Sam Panopoulos is generally credited with putting ham and pineapple together on pizza in Chatham, Ontario, in 1962.',
      headline: 'Canada',
    },
    type: 'multiple-choice',
    options: [
      'United States',
      'Italy',
      'Canada',
      'Australia',
    ],
    answer: 2,
  },
  {
    id: 'wide.310-counting-squares-every-possible-size',
    prompt: 'Counting squares of every possible size, how many squares are on a normal 8×8 chessboard?',
    category: 'Logic',
    difficulty: 4,
    reveal: {
      explanation: 'There are 64 little squares, but also larger ones: 64 + 49 + 36 + 25 + 16 + 9 + 4 + 1 = 204 The board contains far more than the obvious 64.',
      headline: '204',
    },
    type: 'numeric',
    answer: 204,
    displayAnswer: '204',
    magnitude: 'small',
  },
  {
    id: 'wide.311-ordinary-room-temperature-window-glass',
    prompt: 'At ordinary room temperature, window glass is best described as:',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Old windows aren\'t thicker at the bottom because their glass slowly flowed downward. Glass at room temperature behaves as an amorphous solid, and its flow is far too slow to explain medieval window shapes.',
      headline: 'An amorphous solid',
    },
    type: 'multiple-choice',
    options: [
      'A very slow-moving liquid',
      'An amorphous solid',
      'A crystalline solid',
      'A gel',
    ],
    answer: 1,
  },
  {
    id: 'wide.312-word-robot-comes-from-czech',
    prompt: 'The word robot comes from a Czech word originally associated with what?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Robot comes from Czech robota, meaning compulsory or forced labor. The word entered international use through Karel Čapek\'s play R.U.R.',
      headline: 'Forced labor',
    },
    type: 'multiple-choice',
    options: [
      'Metal',
      'Intelligence',
      'Forced labor',
      'Machinery',
    ],
    answer: 2,
  },
  {
    id: 'wide.313-how-many-sheets-standard-modern',
    prompt: 'How many sheets are in a standard modern ream of office paper?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'The modern standard ream contains 500 sheets. Older and specialty definitions sometimes used different quantities.',
      headline: '500',
    },
    type: 'numeric',
    answer: 500,
    displayAnswer: '500',
    magnitude: 'small',
  },
  {
    id: 'wide.314-what-material-used-outer-shell',
    prompt: 'What material was used for the outer shell of the first computer mouse prototype?',
    category: 'Technology',
    difficulty: 3,
    reveal: {
      explanation: 'Douglas Engelbart\'s early mouse prototype was a small wooden box on wheels. The first prototype dates to 1964.',
      headline: 'Wood',
    },
    type: 'multiple-choice',
    options: [
      'Aluminum',
      'Rubber',
      'Wood',
      'Ceramic',
    ],
    answer: 2,
  },
  {
    id: 'wide.315-which-cocoa-derived-ingredient-used',
    prompt: 'Which cocoa-derived ingredient is used to make white chocolate?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'White chocolate contains cocoa butter, along with ingredients such as sugar and dairy, but not the cocoa solids that give milk and dark chocolate their brown color.',
      headline: 'Cocoa butter',
    },
    type: 'multiple-choice',
    options: [
      'Cocoa powder',
      'Cocoa butter',
      'Roasted cocoa shell',
      'Cocoa juice',
    ],
    answer: 1,
  },
  {
    id: 'wide.316-how-many-official-languages-does',
    prompt: 'How many official languages does the United Nations have?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'They are Arabic, Chinese, English, French, Russian and Spanish.',
      headline: '6',
    },
    type: 'numeric',
    answer: 6,
    displayAnswer: '6',
    magnitude: 'small',
  },
  {
    id: 'wide.317-why-ridged-edges-historically-added',
    prompt: 'Why were ridged edges historically added to many gold and silver coins?',
    category: 'History',
    difficulty: 3,
    reveal: {
      explanation: 'People could secretly shave precious metal from smooth-edged coins and still spend them at face value. Reeding made that tampering much easier to detect.',
      headline: 'To reveal if someone had shaved metal from the edge',
    },
    type: 'multiple-choice',
    options: [
      'To make them easier to stack',
      'To reveal if someone had shaved metal from the edge',
      'To make them easier to count',
      'To stop them rolling',
    ],
    answer: 1,
  },
  {
    id: 'wide.318-what-does-japanese-word-karaoke',
    prompt: 'What does the Japanese word karaoke literally mean?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'It combines Japanese kara — empty — with an abbreviation of orchestra.',
      headline: 'Empty orchestra',
    },
    type: 'multiple-choice',
    options: [
      'Singing room',
      'Empty orchestra',
      'Recorded voice',
      'Night music',
    ],
    answer: 1,
  },
  {
    id: 'wide.319-expression-ok-originally-came-from',
    prompt: 'The expression “OK” originally came from a deliberately misspelled version of which phrase?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'A Boston abbreviation craze in the 1830s produced “oll korrect” → O.K. Most of the era\'s joke abbreviations vanished. This one conquered the world.',
      headline: 'All correct',
    },
    type: 'multiple-choice',
    options: [
      'All correct',
      'Only kidding',
      'Order known',
      'Officially confirmed',
    ],
    answer: 0,
  },
  {
    id: 'wide.320-how-many-letters-standard-greek',
    prompt: 'How many letters are in the standard Greek alphabet?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'It runs from alpha to omega, with 24 letters in the standard alphabet.',
      headline: '24',
    },
    type: 'numeric',
    answer: 24,
    displayAnswer: '24',
    magnitude: 'small',
  },
  {
    id: 'wide.321-what-number-has-letters-alphabetical',
    prompt: 'What number has its letters in alphabetical order when its English name is written out?',
    category: 'Logic',
    difficulty: 3,
    reveal: {
      explanation: 'FORTY F → O → R → T → Y Each successive letter comes later in the alphabet.',
      headline: '40',
    },
    type: 'numeric',
    answer: 40,
    displayAnswer: '40',
    magnitude: 'small',
  },
  {
    id: 'wide.322-which-city-farther-north-london',
    prompt: 'Which city is farther north?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'London sits at roughly 51.5° N, while Berlin is around 52.5° N. They\'re closer in latitude than many people picture, but Berlin is farther north.',
      headline: 'Berlin',
    },
    type: 'which-is-closer',
    options: [
      {
        label: 'London',
      },
      {
        label: 'Berlin',
      },
    ],
    answer: 1,
  },
  {
    id: 'wide.323-japanese-word-emoji-formed-from',
    prompt: 'The Japanese word emoji is formed from words meaning what?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Japanese e means picture or drawing, while moji means character or letter. Its resemblance to the English word emotion is coincidental.',
      headline: 'Picture + character',
    },
    type: 'multiple-choice',
    options: [
      'Emotion + symbol',
      'Picture + character',
      'Face + feeling',
      'Message + drawing',
    ],
    answer: 1,
  },
  {
    id: 'wide.324-what-material-lego-group-earliest',
    prompt: 'What material were the LEGO Group\'s earliest toys primarily made from?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Ole Kirk Kristiansen began producing wooden toys in 1932. Plastic LEGO bricks didn\'t arrive until much later.',
      headline: 'Wood',
    },
    type: 'multiple-choice',
    options: [
      'Plastic',
      'Tin',
      'Wood',
      'Rubber',
    ],
    answer: 2,
  },
  {
    id: 'wide.326-from-normal-starting-position-chess',
    prompt: 'From the normal starting position in chess, how many different legal first moves can White make?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Each of the eight pawns has two possible opening moves, giving 16, and the two knights each have two, giving another 4. Total: 20.',
      headline: '20',
    },
    type: 'numeric',
    answer: 20,
    displayAnswer: '20',
    magnitude: 'small',
  },
  {
    id: 'wide.327-what-maximum-number-colors-needed',
    prompt: 'What is the maximum number of colors needed to color any ordinary flat map so that no two regions sharing a border have the same color?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'This is the famous Four Color Theorem. No matter how complicated the map becomes, four colors are enough, provided regions that only touch at a point don\'t count as neighbors.',
      headline: '4',
    },
    type: 'numeric',
    answer: 4,
    displayAnswer: '4',
    magnitude: 'small',
  },
  {
    id: 'wide.328-how-many-sides-does-bius',
    prompt: 'How many sides does a Möbius strip have?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Start tracing one surface without lifting your finger and you\'ll eventually return to where you began having covered the entire strip. It also has just one edge.',
      headline: '1',
    },
    type: 'numeric',
    answer: 1,
    displayAnswer: '1',
    magnitude: 'small',
  },
  {
    id: 'wide.330-when-cooking-with-ordinary-aluminum',
    prompt: 'When cooking with ordinary aluminum foil, which side should normally face the food?',
    category: 'Food',
    difficulty: 2,
    reveal: {
      explanation: 'For ordinary foil, the shiny and dull sides cook essentially the same. The two finishes come from how foil is rolled during manufacturing. Special non-stick foil is an exception.',
      headline: 'Either one',
    },
    type: 'multiple-choice',
    options: [
      'Shiny',
      'Dull',
      'Either one',
    ],
    answer: 2,
  },
  {
    id: 'wide.331-why-does-your-recorded-voice',
    prompt: 'Why does your recorded voice usually sound different from the voice you hear while speaking?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'While speaking, you hear yourself through both air conduction and vibrations through your head. A recording mostly gives you the air-conducted version, which tends to sound less deep and familiar.',
      headline: 'You normally hear part of your voice through your skull',
    },
    type: 'multiple-choice',
    options: [
      'Microphones raise the pitch',
      'You normally hear part of your voice through your skull',
      'Your ears temporarily change sensitivity while speaking',
      'Recordings remove echoes from your mouth',
    ],
    answer: 1,
  },
  {
    id: 'wide.332-what-does-gasoline-octane-rating',
    prompt: 'What does a gasoline\'s octane rating mainly describe?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'Higher octane means the fuel is more resistant to unwanted spontaneous ignition under pressure. It doesn\'t simply mean “more powerful fuel.”',
      headline: 'How resistant it is to engine knock',
    },
    type: 'multiple-choice',
    options: [
      'How much energy it contains',
      'How cleanly it burns',
      'How resistant it is to engine knock',
      'How quickly it evaporates',
    ],
    answer: 2,
  },
  {
    id: 'wide.333-why-does-food-rotate-inside',
    prompt: 'Why does food rotate inside many microwave ovens?',
    category: 'Food',
    difficulty: 2,
    reveal: {
      explanation: 'The microwave field forms uneven hot and cold spots. Rotating the food spreads its exposure across those regions and makes heating more even.',
      headline: 'To move it through hotter and cooler regions of microwave energy',
    },
    type: 'multiple-choice',
    options: [
      'To keep food from sticking to the plate',
      'To move it through hotter and cooler regions of microwave energy',
      'To circulate hot air around it',
      'To stop the microwaves from escaping',
    ],
    answer: 1,
  },
  {
    id: 'wide.335-what-one-most-important-jobs',
    prompt: 'What is one of the most important jobs of the grooves in car tire tread?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'Tread grooves give water somewhere to escape, helping the rubber stay in contact with the road and reducing the risk of hydroplaning.',
      headline: 'Channel water away from under the tire',
    },
    type: 'multiple-choice',
    options: [
      'Trap warm air',
      'Channel water away from under the tire',
      'Make the tire softer',
      'Increase its diameter',
    ],
    answer: 1,
  },
  {
    id: 'wide.336-what-actually-tells-gasoline-pump',
    prompt: 'What actually tells a gasoline pump nozzle to automatically click off when your tank becomes full?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'A small sensing opening near the nozzle tip is part of a pressure/vacuum system. When rising fuel covers it, the change triggers the mechanical shutoff.',
      headline: 'Rising fuel blocks a small sensing opening in the nozzle',
    },
    type: 'multiple-choice',
    options: [
      'Your car sends an electronic signal',
      'A timer inside the pump',
      'Rising fuel blocks a small sensing opening in the nozzle',
      'The nozzle detects the weight of the fuel',
    ],
    answer: 2,
  },
  {
    id: 'wide.337-what-between-inner-outer-walls',
    prompt: 'What is between the inner and outer walls of a traditional vacuum flask or Thermos?',
    category: 'Everyday',
    difficulty: 1,
    reveal: {
      explanation: 'Removing most of the air greatly reduces conduction and convection, which is why the same basic design can keep drinks hot or cold for hours.',
      headline: 'A near-vacuum',
    },
    type: 'multiple-choice',
    options: [
      'Compressed air',
      'Water',
      'A near-vacuum',
      'Cotton',
    ],
    answer: 2,
  },
  {
    id: 'wide.338-what-polarized-sunglasses-primarily-designed',
    prompt: 'What are polarized sunglasses primarily designed to reduce?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Polarization is especially useful against strong glare reflecting from roads, water and other roughly horizontal surfaces. UV protection is a separate property.',
      headline: 'Glare reflected from surfaces',
    },
    type: 'multiple-choice',
    options: [
      'Blue light from screens',
      'Glare reflected from surfaces',
      'All ultraviolet radiation',
      'Peripheral vision',
    ],
    answer: 1,
  },
  {
    id: 'wide.339-why-does-normal-pair-fabric',
    prompt: 'Why does a normal pair of fabric gloves often stop a smartphone touchscreen from responding?',
    category: 'Technology',
    difficulty: 2,
    reveal: {
      explanation: 'Most smartphones use capacitive touchscreens. Ordinary fabric insulates your finger, while touchscreen-compatible gloves add conductive material that lets the screen detect the touch.',
      headline: 'The screen needs electrical interaction from a conductive object',
    },
    type: 'multiple-choice',
    options: [
      'The screen detects body heat',
      'The screen needs electrical interaction from a conductive object',
      'The fabric blocks infrared light',
      'The phone detects fingerprints',
    ],
    answer: 1,
  },
  {
    id: 'wide.340-what-substance-does-laser-printer',
    prompt: 'What substance does a laser printer put onto paper to create the image?',
    category: 'Technology',
    difficulty: 2,
    reveal: {
      explanation: 'A laser printer uses static charge to position toner powder, then hot rollers melt and fuse it onto the paper.',
      headline: 'Powdered toner',
    },
    type: 'multiple-choice',
    options: [
      'Liquid ink',
      'Powdered toner',
      'Graphite',
      'Wax',
    ],
    answer: 1,
  },
  {
    id: 'wide.342-normal-flat-mirror-physically-reverses',
    prompt: 'A normal flat mirror physically reverses which direction?',
    category: 'Science',
    difficulty: 3,
    reveal: {
      explanation: 'Your left side remains on the left side of the mirror image. What actually reverses is the axis perpendicular to the mirror — toward the mirror versus away from it.',
      headline: 'Front and back',
    },
    type: 'multiple-choice',
    options: [
      'Left and right',
      'Up and down',
      'Front and back',
    ],
    answer: 2,
  },
  {
    id: 'wide.343-how-many-different-basic-piece',
    prompt: 'How many different basic piece shapes are there in standard Tetris?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Every piece is made from four squares, and there are seven distinct Tetrimino shapes in the standard game.',
      headline: '7',
    },
    type: 'numeric',
    answer: 7,
    displayAnswer: '7',
    magnitude: 'small',
  },
  {
    id: 'wide.344-what-gas-creates-characteristic-holes',
    prompt: 'What gas creates the characteristic holes in Swiss-style cheese?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Bacteria produce CO₂ during ripening. Bubbles of that gas form the cavities cheesemakers call the cheese\'s “eyes.”',
      headline: 'Carbon dioxide',
    },
    type: 'multiple-choice',
    options: [
      'Oxygen',
      'Nitrogen',
      'Carbon dioxide',
      'Hydrogen',
    ],
    answer: 2,
  },
  {
    id: 'wide.345-why-do-many-crackers-have',
    prompt: 'Why do many crackers have regularly spaced little holes punched into them before baking?',
    category: 'Space',
    difficulty: 2,
    reveal: {
      explanation: 'Bakers call this docking. It keeps large bubbles from puffing the cracker up and helps it stay thin and crisp.',
      headline: 'To let steam escape',
    },
    type: 'multiple-choice',
    options: [
      'To let steam escape',
      'To absorb more salt',
      'To make them break evenly',
      'To cool them faster afterward',
    ],
    answer: 0,
  },
  {
    id: 'wide.346-what-world-largest-country-with',
    prompt: 'What is the world\'s largest country with no permanent natural river?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'Saudi Arabia is enormous, but its surface drainage is dominated by wadis, which carry water intermittently rather than flowing year-round.',
      headline: 'Saudi Arabia',
    },
    type: 'multiple-choice',
    options: [
      'Mongolia',
      'Saudi Arabia',
      'Libya',
      'Kazakhstan',
    ],
    answer: 1,
  },
  {
    id: 'wide.347-which-country-national-flag-has',
    prompt: 'Which country\'s national flag has an officially different emblem on its front and back?',
    category: 'Geography',
    difficulty: 3,
    reveal: {
      explanation: 'One side bears Paraguay\'s national coat of arms; the reverse carries a different treasury seal featuring a lion and the words Paz y Justicia.',
      headline: 'Paraguay',
    },
    type: 'multiple-choice',
    options: [
      'Paraguay',
      'Portugal',
      'Thailand',
      'Croatia',
    ],
    answer: 0,
  },
  {
    id: 'wide.348-what-did-nintendo-originally-manufacture',
    prompt: 'What did Nintendo originally manufacture when the company began in 1889?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'Nintendo began in Kyoto making traditional Japanese hanafuda playing cards — almost a century before the Nintendo Entertainment System.',
      headline: 'Playing cards',
    },
    type: 'multiple-choice',
    options: [
      'Board games',
      'Playing cards',
      'Radios',
      'Mechanical toys',
    ],
    answer: 1,
  },
  {
    id: 'wide.349-before-becoming-children-modeling-toy',
    prompt: 'Before becoming a children\'s modeling toy, the material later sold as Play-Doh was used for what?',
    category: 'Everyday',
    difficulty: 3,
    reveal: {
      explanation: 'The putty was used to remove soot from wallpaper. As cleaner heating systems made that job obsolete, someone realized children could use the same material for modeling.',
      headline: 'Cleaning wallpaper',
    },
    type: 'multiple-choice',
    options: [
      'Sealing windows',
      'Cleaning wallpaper',
      'Repairing shoes',
      'Polishing furniture',
    ],
    answer: 1,
  },
  {
    id: 'wide.350-what-does-40-name-wd',
    prompt: 'What does the 40 in the name WD-40 refer to?',
    category: 'Everyday',
    difficulty: 2,
    reveal: {
      explanation: 'WD stood for Water Displacement. The team finally got the formula they wanted on attempt number 40.',
      headline: 'The successful formula was the 40th attempt',
    },
    type: 'multiple-choice',
    options: [
      'It contains 40 ingredients',
      'It works at temperatures down to −40°',
      'The successful formula was the 40th attempt',
      'The original can held 40 ounces',
    ],
    answer: 2,
  },
];
